import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Cell,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { supabase, veritas, fetchAllPages } from '@/lib/supabase';
import { useCurrentUser } from '@/lib/auth';
import { useRealtimeInserts } from '@/lib/realtime';
import { cn, fmtMoney, fmtPct, fmtRMultiple } from '@/lib/utils';
import {
  Chip, Chips, DistRow, Kpi, LivePill, Panel, PanelBody, PanelHead,
} from '@/components/ui';
import {
  drawdownSeries, recentMonths, riskMetrics,
  type EquityPoint, type RiskMetrics, type TradeRow,
  type DayBucket, type IntradayPoint,
} from '@/lib/metrics';
import {
  alertKindDirection, alertKindLabel, alertKindUnit, evaluateAlertRule,
} from '@/lib/alerts';
import type { TagStatsRow, MistakeRow } from '@/lib/r-multiples';
import {
  fetchTradeAnalytics, fetchBotBreakdown, fetchPositionAnalytics, fetchBehaviorAnalytics,
  kpisFromAnalytics, dailyFromAnalytics, hourlyFromAnalytics, histogramFromAnalytics,
  volumeBucketsFromAnalytics, sourceFromAnalytics, rStatsFromAnalytics, EMPTY_RSTATS, EMPTY_KPIS,
  calendarMapFromAnalytics, intradayFromAnalytics, perTagFromBehavior, mistakesFromBehavior,
  type TradeAnalytics, type BotBreakdownRow, type PositionAnalytics, type BehaviorAnalytics,
} from '@/lib/account-analytics';
import {
  TradeNoteDrawer, TagPicker,
} from '@/components/journal';
import { ThesisPanel } from '@/components/thesis';
import { ExportDialog } from '@/components/export-dialog';
import { ShareDialog } from '@/components/account/share-dialog';
import { PerBotPanel, type BotPerfRow, type BotFlag } from '@/components/account/per-bot-panel';
import { EaBenchmarkPanel } from '@/components/account/ea-benchmark-panel';
import { TradeJournal } from '@/components/account/trade-journal';
import { EquityDrawdownPanel } from '@/components/account/equity-drawdown-panel';
import type {
  AccountSnapshot, AlertKind, AlertRule, ClosedPosition,
  Tag, TradeNote, TradeTag,
} from '@/types/veritas';

// ════════════════════════════════════════════════════════════════════════
// Recharts shared chart styling — keeps the dashboard visually consistent
// without re-declaring axis colors on every chart.
// ════════════════════════════════════════════════════════════════════════
const AXIS = { stroke: '#4b5563', fontSize: 10, fontFamily: 'JetBrains Mono' };
const GRID = '#1a2029';
const TooltipStyle = {
  backgroundColor: '#0f1115',
  border: '1px solid #232a35',
  borderRadius: 6,
  fontSize: 11,
  color: '#f3f4f6',
};

// ════════════════════════════════════════════════════════════════════════
// Page
// ════════════════════════════════════════════════════════════════════════
type CredentialOption = {
  id: string;
  install_label: string;
  revoked_at: string | null;
  owner_user_id: string | null;
};

// One row from veritas.balance_operations. Captures non-trade balance changes
// the broker books against the account: deposits, withdrawals, rebates, etc.
// `category` is heuristic on (deal_type, profit sign, comment); see the M8
// migration for the classification rules.
type BalanceOpRow = {
  id: number;
  credential_id: string;
  account_login: number;
  deal_ticket: number;
  snapshot_ts: string;     // ISO
  received_at: string;     // ISO
  deal_type: number;
  profit: number;
  comment: string | null;
  category:
    | 'deposit' | 'withdrawal' | 'rebate' | 'bonus'
    | 'credit'  | 'charge'     | 'correction'
    | 'interest'| 'dividend'   | 'tax'
    | 'deposit_unclassified'   | 'withdrawal_unclassified'
    | 'other';
  amount: number;          // = profit, sign-normalised: positive inbound, negative outbound
};

// User-asserted "what my account had when Veritas first connected". NOT
// chain-attested — the trader is declaring this number themselves to give
// the trading-return calc an honest baseline that excludes pre-bridge
// funding the chain cannot see. The UI must label it as user-declared.
type StartingCapitalRow = {
  account_login:    number;
  asserted_balance: number;
  as_of_date:       string;          // YYYY-MM-DD
  notes:            string | null;
  created_at:       string;
  created_by:       string | null;
  updated_at:       string;
  updated_by:       string | null;
};

// ════════════════════════════════════════════════════════════════════════
// S8 — Per-Bot Performance
// One row per magic ("bot"), derived entirely client-side from the closed
// positions the page already holds + EA Registry identities (S7). No
// migration, no new read beyond ea_registry. Mirrors the proven SQL in
// tasks/queries/per-bot-performance.sql so the panel and the admin artifact
// agree by definition. Spec: tasks/veritas-bot-performance-spec.md.
// ════════════════════════════════════════════════════════════════════════
// BotFlag / BotPerfRow types now live in components/account/per-bot-panel.tsx
// and are imported above — they're the contract between the botPerf memo here
// (the data derivation) and the presentational panel.

// Per-bot net P&L (cost-inclusive positionNet) + realized DD are now computed
// server-side in veritas.account_bot_breakdown; the COALESCE-to-0 cost handling
// lives there. See src/lib/account-analytics.ts.

// ════════════════════════════════════════════════════════════════════════
// P2 — equity curve performance (see tasks/db-performance-findings.md)
// The old page pulled EVERY raw 30-second snapshot (22-40 paged round-trips)
// just to draw the equity curve. P2 reads the account_snapshots_hourly rollup
// for the bulk of history and only the last RAW_RECENT_DAYS days of raw
// snapshots for recent intraday detail, cutting a page load to ~7 reads.
// ════════════════════════════════════════════════════════════════════════
type HourlySnapshotRow = {
  credential_id: string;
  account_login: number;
  hour_start: string;          // ISO, top of the UTC hour
  balance_open: number | null;
  balance_close: number | null;
  equity_close: number | null;
};

const RAW_RECENT_DAYS = 2; // tunable: 7 ≈ 15 reads on a busy account, 2 ≈ 6.

// One continuous equity series: hourly closes for everything strictly before
// the first raw point, raw snapshots for the recent window. Using the first
// raw point's timestamp as the boundary guarantees no overlap and no gap,
// independent of clock skew between fetch time and render time. numeric
// columns are coerced (PostgREST can return numeric as string).
function buildEquity(
  hourly: HourlySnapshotRow[],
  recentRaw: AccountSnapshot[],
  cutoffMs: number,
): EquityPoint[] {
  const pts: EquityPoint[] = [];
  for (const h of hourly) {
    const tMs = Date.parse(h.hour_start);
    if (!Number.isFinite(tMs) || tMs >= cutoffMs) continue; // recent window owned by raw
    if (h.balance_close == null) continue;
    pts.push({
      t: h.hour_start,
      balance: Number(h.balance_close),
      equity: h.equity_close == null ? null : Number(h.equity_close),
    });
  }
  for (const r of recentRaw) {
    if (r.balance == null) continue;
    pts.push({
      t: r.snapshot_ts,
      balance: Number(r.balance),
      equity: r.equity == null ? null : Number(r.equity),
    });
  }
  pts.sort((a, b) => a.t.localeCompare(b.t));
  return pts;
}

export default function AccountDetailPage() {
  const { login } = useParams<{ login: string }>();
  const accountLogin = Number(login);

  // P2: `snaps` now holds ONLY the recent raw window (last RAW_RECENT_DAYS).
  // `hourly` is the rollup for the long curve; `latestSnap` is the single
  // newest raw row (robust for dormant accounts with no recent snapshots).
  const [snaps, setSnaps] = useState<AccountSnapshot[]>([]);
  const [hourlySnaps, setHourlySnaps] = useState<HourlySnapshotRow[]>([]);
  const [latestSnap, setLatestSnap] = useState<AccountSnapshot | null>(null);
  // Option 2: `trades` / `closedPositions` now hold ONLY the visible journal
  // window (most-recent N, magic+source scoped) — NOT full history. Page
  // aggregates/KPIs come from the server RPCs in `tradeAgg`/`botRows`/`posAgg`/
  // `behavior` below, so a long-history account no longer pulls everything.
  const [trades, setTrades] = useState<TradeRow[]>([]);
  const [closedPositions, setClosedPositions] = useState<ClosedPosition[]>([]);
  const [journalLimit, setJournalLimit] = useState(100);
  const [dealCount, setDealCount] = useState(0);
  const [posCount, setPosCount] = useState(0);
  const [journalLoading, setJournalLoading] = useState(false);
  const [tradeAgg, setTradeAgg] = useState<TradeAnalytics | null>(null);
  const [botRows, setBotRows] = useState<BotBreakdownRow[]>([]);
  const [posAgg, setPosAgg] = useState<PositionAnalytics | null>(null);
  const [behavior, setBehavior] = useState<BehaviorAnalytics | null>(null);
  // Bumped by a realtime trade insert to trigger an aggregate + journal refetch.
  const [refreshTick, setRefreshTick] = useState(0);
  const [alertRules, setAlertRules] = useState<AlertRule[]>([]);
  const [notes, setNotes] = useState<TradeNote[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [tradeTags, setTradeTags] = useState<TradeTag[]>([]);
  // Non-trade balance changes: deposits, withdrawals, rebates, etc. Sourced
  // from veritas.balance_operations (sister view to trade_fills). Surfaced
  // in the metadata strip + as reference markers on the equity curve so a
  // $10K deposit doesn't read as trading edge.
  const [balanceOps, setBalanceOps] = useState<BalanceOpRow[]>([]);
  // Net non-trade balance flows booked in the current broker day, keyed by
  // credential, from the single-source view veritas.account_today_pnl. Used to
  // derive trading-only Today P&L for the daily_loss alert so a same-day
  // deposit can't mask a real intraday loss (nor a withdrawal falsely trip it).
  const [todayOpsByCred, setTodayOpsByCred] = useState<Map<string, number>>(new Map());
  // User-asserted starting capital — sets the baseline for trading-return
  // calcs. Null until the trader declares it.
  const [startingCapital, setStartingCapital] = useState<StartingCapitalRow | null>(null);
  // Server-computed Max Drawdown (capital-flow-adjusted, equity-inclusive) from
  // veritas.account_max_drawdown over RAW snapshots. The client equity series is
  // hourly-sampled and balance-only, which both understates DD and (when
  // deposit-adjusting) misaligns across the rollup's hour buckets — so we get
  // the authoritative number from the DB. Null until loaded → card falls back
  // to the client estimate.
  const [serverDd, setServerDd] = useState<{
    maxDdPct: number; maxDdAbs: number;            // equity/total (balance + floating)
    realizedDdPct: number; realizedDdAbs: number;  // balance / closed trades
    floatingDdPct: number; floatingDdAbs: number;  // peak open-position underwater
  } | null>(null);
  // Daily deposit-adjusted, equity-inclusive drawdown series for the chart, from
  // veritas.account_drawdown_series. Same basis as serverDd so the chart trough
  // matches the card. Null until loaded → chart falls back to the client series.
  const [ddSeriesServer, setDdSeriesServer] = useState<Array<{ bucket: string; ddPct: number; ddAbs: number }> | null>(null);

  // Modal state — which position has the note drawer / tag picker open
  type OpenTarget = {
    account_login: number; position_ticket: number;
    credential_id: string; symbol: string; closed_at: string | null;
  };
  const [openNote, setOpenNote] = useState<OpenTarget | null>(null);
  const [openTags, setOpenTags] = useState<OpenTarget | null>(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const { user } = useCurrentUser();

  // Index trade_tags by position_ticket so the table renders in O(1)/row
  const positionTagsMap = useMemo(() => {
    const m = new Map<number, Set<string>>();
    for (const tt of tradeTags) {
      let s = m.get(tt.position_ticket);
      if (!s) { s = new Set(); m.set(tt.position_ticket, s); }
      s.add(tt.tag_id);
    }
    return m;
  }, [tradeTags]);

  const [credentials, setCredentials] = useState<CredentialOption[]>([]);
  // Owner-or-admin. Gates EVERY edit affordance on this page (sharing, title,
  // starting capital, notes, tags, thesis, alerts). A viewer with shared
  // read-only access gets a fully static page; every write is also RLS-blocked
  // server-side, so this is UX, not the security boundary. owner_user_id is
  // uniform across an account's credentials.
  const accountOwnerId = credentials.find((c) => c.owner_user_id)?.owner_user_id ?? null;
  const canEdit = !!user && (user.isAdmin || (accountOwnerId != null && accountOwnerId === user.id));
  // 'all' shows merged data across credentials; otherwise a credential UUID.
  const [selectedCredId, setSelectedCredId] = useState<string>('all');
  // Source filter scopes the journal + behavior panels (NOT the KPIs / charts,
  // which always reflect the full account state). 'manual' is the right
  // default for a trader who wants to focus on their own decisions and
  // ignore deterministic bot trades.
  const [selectedSource, setSelectedSource] = useState<'all' | 'manual' | 'bot'>('all');
  // Magic filter — picks ONE bot's trades (or "all magics" = no filter). Unlike
  // the Source filter, this scopes the WHOLE trade-derived view (KPIs, charts,
  // journal, behavior) so the user can answer "how is bot X performing on this
  // account?". Snapshot-based panels (equity, drawdown, risk-adjusted) stay at
  // account level since snapshots aren't per-bot.
  const [selectedMagic, setSelectedMagic] = useState<number | 'all'>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters are account-scoped state, but the component stays mounted when the
  // route param changes (/accounts/A → /accounts/B). A magic number, credential
  // UUID or source pick carried over from the previous account silently empties
  // the journal on the next one — it reads like data loss. Reset the whole
  // class, not just magic (stale credential UUIDs cross accounts too).
  useEffect(() => {
    setSelectedMagic('all');
    setSelectedSource('all');
    setSelectedCredId('all');
  }, [accountLogin]);

  // EA Registry identities (S7), keyed by magic → { name, version }. Resolves
  // a magic number to the EA's self-reported name + version for the Per-Bot
  // panel. Empty until the registry has a row for a bot; the panel falls back
  // to "Manual" / "Unregistered (magic N)" so it never blocks on registry data.
  const [eaIdentities, setEaIdentities] =
    useState<Map<number, { name: string; version: string | null }>>(new Map());

  // Fetch the credential list once per account so the filter chips know
  // their options. Cheap (≤ a handful of rows per account).
  useEffect(() => {
    let cancelled = false;
    async function loadCreds() {
      const r = await veritas
        .from('ea_credentials')
        .select('id,install_label,revoked_at,created_at,owner_user_id')
        .eq('account_login', accountLogin)
        .order('created_at', { ascending: true });
      if (cancelled) return;
      setCredentials((r.data ?? []) as CredentialOption[]);
      // owner_user_id is uniform across an account's credentials; first non-null wins.
    }
    loadCreds();
    return () => { cancelled = true; };
  }, [accountLogin]);

  // EA Registry identities (S7) for the Per-Bot panel. Account-level, one row
  // per (chart, magic) — bounded well under the 1000-row cap, so a single
  // select is safe (no pagination needed; see fetchAllPages contract / L11).
  // Ordered first_seen DESC so the first row seen for a magic is its latest
  // identity. Failure is non-fatal: the panel just shows raw magic labels.
  useEffect(() => {
    let cancelled = false;
    async function loadIdentities() {
      const r = await veritas
        .from('ea_registry')
        .select('magic_number,ea_name,ea_version,first_seen')
        .eq('account_login', accountLogin)
        .order('first_seen', { ascending: false });
      if (cancelled) return;
      const m = new Map<number, { name: string; version: string | null }>();
      for (const row of (r.data ?? []) as Array<{
        magic_number: number; ea_name: string; ea_version: string | null;
      }>) {
        if (!m.has(row.magic_number)) {
          m.set(row.magic_number, { name: row.ea_name, version: row.ea_version });
        }
      }
      setEaIdentities(m);
    }
    loadIdentities();
    return () => { cancelled = true; };
  }, [accountLogin]);

  // Per-(user, account_login) display label + category (M2 — see
  // tasks/account-user-metadata-spec.md). RLS on account_user_metadata gates
  // the read to the caller's own row; absent → fall back to "Account {login}".
  // Categories list powers the editor's <datalist> autocomplete.
  const [userMeta, setUserMeta] = useState<{
    display_label: string | null;
    category:      string | null;
    single_system: boolean;
    equity_protector_mode:  'pct' | 'abs' | null;
    equity_protector_value: number | null;
  } | null>(null);
  const [userCategories, setUserCategories] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [mRes, cRes] = await Promise.all([
        veritas
          .from('account_user_metadata')
          .select('display_label,category,single_system,equity_protector_mode,equity_protector_value')
          .eq('account_login', accountLogin)
          .maybeSingle(),
        veritas.rpc('list_my_account_categories'),
      ]);
      if (cancelled) return;
      setUserMeta(
        (mRes.data as {
          display_label: string | null; category: string | null; single_system: boolean;
          equity_protector_mode: 'pct' | 'abs' | null; equity_protector_value: number | null;
        } | null)
          ?? {
            display_label: null, category: null, single_system: false,
            equity_protector_mode: null, equity_protector_value: null,
          },
      );
      setUserCategories(
        (((cRes.data as Array<{ category: string }> | null) ?? [])).map((r) => r.category),
      );
    })();
    return () => { cancelled = true; };
  }, [accountLogin]);

  // Re-fetch snapshots + trades whenever the account or selected credential
  // changes. Adding `.eq('credential_id', selectedCredId)` to the query when
  // a specific credential is picked lets PostgREST + the index do the
  // filtering — no client-side post-processing needed.
  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      // Every per-account table below is read through fetchAllPages() because
      // PostgREST silently caps a single response at 1000 rows (see the helper
      // above). account_snapshots, trade_fills, and closed_positions all
      // routinely exceed that on an active account; balance_operations rarely
      // does, but is paginated too so it can never be quietly truncated either.
      // P2: long curve from the hourly rollup (~300 rows/account = 1 page).
      const hourlyQ = fetchAllPages<HourlySnapshotRow>((from, to) => {
        let q = veritas
          .from('account_snapshots_hourly')
          .select('credential_id,account_login,hour_start,balance_open,balance_close,equity_close')
          .eq('account_login', accountLogin)
          .order('hour_start', { ascending: true })
          .order('credential_id', { ascending: true }) // unique tiebreaker (PK = credential_id, hour_start)
          .range(from, to);
        if (selectedCredId !== 'all') q = q.eq('credential_id', selectedCredId);
        return q;
      });
      // P2: only the recent raw window for intraday detail (bounded → few pages).
      const recentCutoffIso = new Date(Date.now() - RAW_RECENT_DAYS * 86400000).toISOString();
      const recentRawQ = fetchAllPages<AccountSnapshot>((from, to) => {
        let q = veritas
          .from('account_snapshots')
          .select('*')
          .eq('account_login', accountLogin)
          .gte('snapshot_ts', recentCutoffIso)
          .order('snapshot_ts', { ascending: true })
          .order('id', { ascending: true }) // unique tiebreaker — see fetchAllPages contract
          .range(from, to);
        if (selectedCredId !== 'all') q = q.eq('credential_id', selectedCredId);
        return q;
      });
      // P2: single newest raw snapshot — drives the live pills even when the
      // account has been dormant longer than the recent window.
      const latestSnapQ = (() => {
        let q = veritas
          .from('account_snapshots')
          .select('*')
          .eq('account_login', accountLogin)
          .order('snapshot_ts', { ascending: false })
          .limit(1);
        if (selectedCredId !== 'all') q = q.eq('credential_id', selectedCredId);
        return q.maybeSingle();
      })();
      // Option 2: trade_fills + closed_positions are NO LONGER pulled in full
      // here. Aggregates come from the analytics RPCs (separate effect below) and
      // the journal fetches only a recent window. This is the load/latency fix.
      // S1: balance_operations is non-trade balance flows (deposits, withdrawals,
      // rebates, etc.). Usually a handful of rows per account — paginated anyway
      // so a high-activity account can never silently lose flows.
      const balanceOpsQ = fetchAllPages<BalanceOpRow>((from, to) => {
        let q = veritas
          .from('balance_operations')
          .select('*')
          .eq('account_login', accountLogin)
          .order('snapshot_ts', { ascending: true })
          .order('id', { ascending: true }) // unique tiebreaker — see fetchAllPages contract
          .range(from, to);
        if (selectedCredId !== 'all') q = q.eq('credential_id', selectedCredId);
        return q;
      });

      // User-asserted starting capital. One row per account_login (or none).
      // maybeSingle() returns data=null if not declared yet, no error.
      const startCapQ = veritas
        .from('account_starting_capital')
        .select('*')
        .eq('account_login', accountLogin)
        .maybeSingle();

      // Trading-only Today P&L input — single source: veritas.account_today_pnl.
      // We only pull the per-credential balance-ops component; the alert below
      // recomputes trading P&L live as (latest.day_pnl - ops) so it stays
      // current across realtime snapshot inserts without re-fetching the view.
      const todayPnlQ = fetchAllPages<{ credential_id: string; day_balance_ops: string | number | null }>((from, to) => {
        let q = veritas
          .from('account_today_pnl')
          .select('credential_id,day_balance_ops')
          .eq('account_login', accountLogin)
          .order('credential_id', { ascending: true })
          .range(from, to);
        if (selectedCredId !== 'all') q = q.eq('credential_id', selectedCredId);
        return q;
      });

      // Authoritative Max Drawdown over RAW snapshots (deposit-adjusted,
      // equity-inclusive). Scoped to the selected credential when one is picked.
      const ddQ = veritas.rpc('account_max_drawdown', {
        p_account_login: accountLogin,
        p_credential_id: selectedCredId === 'all' ? null : selectedCredId,
      });
      // Daily deposit-adjusted drawdown series for the chart (same basis as ddQ).
      const ddSeriesQ = veritas.rpc('account_drawdown_series', {
        p_account_login: accountLogin,
        p_credential_id: selectedCredId === 'all' ? null : selectedCredId,
      });

      const [hourlyRes, recentRes, latestRes, balanceOpsRes, startCapRes, todayPnlRes, ddRes, ddSeriesRes] = await Promise.all([
        hourlyQ, recentRawQ, latestSnapQ, balanceOpsQ, startCapQ, todayPnlQ, ddQ, ddSeriesQ,
      ]);
      if (cancelled) return;
      if (hourlyRes.error)     setError(hourlyRes.error.message);
      if (recentRes.error)     setError(recentRes.error.message);
      if (latestRes.error)     setError(latestRes.error.message);
      if (balanceOpsRes.error) setError(balanceOpsRes.error.message);
      if (startCapRes.error)   setError(startCapRes.error.message);
      if (todayPnlRes.error)   setError(todayPnlRes.error.message);
      setHourlySnaps((hourlyRes.data ?? []) as HourlySnapshotRow[]);
      setSnaps((recentRes.data ?? []) as AccountSnapshot[]);
      setLatestSnap((latestRes.data ?? null) as AccountSnapshot | null);
      setBalanceOps((balanceOpsRes.data ?? []) as BalanceOpRow[]);
      setStartingCapital((startCapRes.data ?? null) as StartingCapitalRow | null);
      {
        const opsMap = new Map<string, number>();
        for (const t of (todayPnlRes.data ?? []) as Array<{ credential_id: string; day_balance_ops: string | number | null }>) {
          opsMap.set(t.credential_id, Number(t.day_balance_ops ?? 0));
        }
        setTodayOpsByCred(opsMap);
      }
      if (ddRes.error) setError(ddRes.error.message);
      {
        const d = (ddRes.data as Array<{
          max_dd_pct: string | number; max_dd_abs: string | number;
          realized_dd_pct: string | number; realized_dd_abs: string | number;
          floating_dd_pct: string | number; floating_dd_abs: string | number;
        }> | null)?.[0];
        setServerDd(d ? {
          maxDdPct: Number(d.max_dd_pct),
          maxDdAbs: Number(d.max_dd_abs),
          realizedDdPct: Number(d.realized_dd_pct),
          realizedDdAbs: Number(d.realized_dd_abs),
          floatingDdPct: Number(d.floating_dd_pct),
          floatingDdAbs: Number(d.floating_dd_abs),
        } : null);
      }
      if (ddSeriesRes.error) setError(ddSeriesRes.error.message);
      setDdSeriesServer(
        ((ddSeriesRes.data as Array<{ bucket: string; dd_pct: string | number; dd_abs: string | number }> | null) ?? [])
          .map((row) => ({ bucket: row.bucket, ddPct: Number(row.dd_pct), ddAbs: Number(row.dd_abs) })),
      );
      setLoading(false);
    }
    load();
    return () => { cancelled = true; };
  }, [accountLogin, selectedCredId]);

  // Option 2: server-side aggregates. Refetch on account / credential / magic /
  // source change and when a realtime fill bumps refreshTick. These replace the
  // old full-array client memos (computeKpis, dailyAggregates, per-bot, r-stats,
  // behavior, …) — each verified to the cent vs the JS originals.
  useEffect(() => {
    let cancelled = false;
    const cred = selectedCredId === 'all' ? null : selectedCredId;
    const magic = selectedMagic === 'all' ? null : selectedMagic;
    (async () => {
      try {
        const [ta, br, pa, ba] = await Promise.all([
          fetchTradeAnalytics(accountLogin, cred, magic),
          fetchBotBreakdown(accountLogin, cred),
          fetchPositionAnalytics(accountLogin, cred, magic, selectedSource),
          fetchBehaviorAnalytics(accountLogin, cred, magic, selectedSource),
        ]);
        if (cancelled) return;
        setTradeAgg(ta);
        setBotRows(br);
        setPosAgg(pa);
        setBehavior(ba);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => { cancelled = true; };
  }, [accountLogin, selectedCredId, selectedMagic, selectedSource, refreshTick]);

  // Reset the journal window whenever the scope changes.
  useEffect(() => {
    setJournalLimit(100);
  }, [accountLogin, selectedCredId, selectedMagic, selectedSource]);

  // Journal window — most-recent `journalLimit` deals + positions, scoped by
  // credential + magic + source server-side. "Load more" raises journalLimit.
  // {count:'exact'} powers the "showing N of M" / load-more affordance.
  useEffect(() => {
    let cancelled = false;
    const cred = selectedCredId === 'all' ? null : selectedCredId;
    setJournalLoading(true);
    (async () => {
      let dealsQ = veritas
        .from('trade_fills')
        .select('deal_ticket,snapshot_ts,symbol,deal_type,deal_reason,magic,volume,profit,credential_id', { count: 'exact' })
        .eq('account_login', accountLogin);
      if (cred) dealsQ = dealsQ.eq('credential_id', cred);
      if (selectedMagic !== 'all') dealsQ = dealsQ.eq('magic', selectedMagic);
      if (selectedSource === 'bot') dealsQ = dealsQ.neq('magic', 0);
      else if (selectedSource === 'manual') dealsQ = dealsQ.eq('magic', 0);
      dealsQ = dealsQ
        .order('snapshot_ts', { ascending: false })
        .order('deal_ticket', { ascending: false })
        .range(0, journalLimit - 1);

      let posQ = veritas
        .from('closed_positions')
        .select('*', { count: 'exact' })
        .eq('account_login', accountLogin);
      if (cred) posQ = posQ.eq('credential_id', cred);
      if (selectedMagic !== 'all') posQ = posQ.eq('magic', selectedMagic);
      if (selectedSource === 'bot') posQ = posQ.neq('magic', 0);
      else if (selectedSource === 'manual') posQ = posQ.eq('magic', 0);
      posQ = posQ
        .order('closed_at', { ascending: false })
        .order('position_ticket', { ascending: false })
        .range(0, journalLimit - 1);

      const [dRes, pRes] = await Promise.all([dealsQ, posQ]);
      if (cancelled) return;
      if (dRes.error) setError(dRes.error.message);
      if (pRes.error) setError(pRes.error.message);
      setTrades((dRes.data ?? []) as TradeRow[]);
      setClosedPositions((pRes.data ?? []) as ClosedPosition[]);
      setDealCount(dRes.count ?? 0);
      setPosCount(pRes.count ?? 0);
      setJournalLoading(false);
    })();
    return () => { cancelled = true; };
  }, [accountLogin, selectedCredId, selectedMagic, selectedSource, journalLimit, refreshTick]);

  // Alert rules — load once per credential-filter change, then realtime
  // appends. Rules only exist for a specific credential, so when the user
  // picks 'all' we show rules from every credential on this account.
  useEffect(() => {
    let cancelled = false;
    async function loadRules() {
      const credIds = credentials.map((c) => c.id);
      if (credIds.length === 0) { setAlertRules([]); return; }
      const r = await fetchAllPages<AlertRule>((from, to) => {
        const q = veritas.from('alert_rules').select('*');
        if (selectedCredId !== 'all') q.eq('credential_id', selectedCredId);
        else q.in('credential_id', credIds);
        return q
          .order('created_at', { ascending: true })
          .order('id', { ascending: true }) // unique tiebreaker — see fetchAllPages contract
          .range(from, to);
      });
      if (cancelled) return;
      if (r.error) { setError(r.error.message); return; }
      setAlertRules((r.data ?? []) as AlertRule[]);
    }
    loadRules();
    return () => { cancelled = true; };
  }, [credentials, selectedCredId]);

  // Journal data — notes (per position) + tags (per user, including globals)
  // + trade_tags (assignments). Loaded together since the Positions table
  // needs all three.
  useEffect(() => {
    let cancelled = false;
    async function loadJournal() {
      const [notesRes, tagsRes, tradeTagsRes] = await Promise.all([
        fetchAllPages<TradeNote>((from, to) =>
          veritas
            .from('trade_notes')
            .select('*')
            .eq('account_login', accountLogin)
            .order('position_ticket', { ascending: true }) // unique within account — see fetchAllPages contract
            .range(from, to)),
        fetchAllPages<Tag>((from, to) =>
          veritas
            .from('tags')
            .select('*')
            .order('name', { ascending: true })
            .order('id', { ascending: true }) // unique tiebreaker — see fetchAllPages contract
            .range(from, to)),
        fetchAllPages<TradeTag>((from, to) =>
          veritas
            .from('trade_tags')
            .select('*')
            .eq('account_login', accountLogin)
            .order('position_ticket', { ascending: true })
            .order('tag_id', { ascending: true }) // (position_ticket,tag_id) unique within account — see fetchAllPages contract
            .range(from, to)),
      ]);
      if (cancelled) return;
      if (notesRes.error)     setError(notesRes.error.message);
      if (tagsRes.error)      setError(tagsRes.error.message);
      if (tradeTagsRes.error) setError(tradeTagsRes.error.message);
      setNotes((notesRes.data ?? []) as TradeNote[]);
      setTags((tagsRes.data ?? []) as Tag[]);
      setTradeTags((tradeTagsRes.data ?? []) as TradeTag[]);
    }
    loadJournal();
    return () => { cancelled = true; };
  }, [accountLogin]);
  // can see, so we only get pushed our own data.
  //
  // PostgREST filter syntax — we pin to account_login so the channel doesn't
  // also fire for other accounts. When a credential is picked we add another
  // server-side filter; when 'all', the credential filter is omitted and we
  // rely on the snapshot/trade row's account_login alone.
  const realtimeFilter = `account_login=eq.${accountLogin}`;

  useRealtimeInserts<AccountSnapshot>(
    'veritas',
    'account_snapshots',
    realtimeFilter,
    (row) => {
      // Honor the per-credential filter on the client. Cheap because we
      // already filter at the server for account_login; this just drops
      // the cross-credential rows when the user picked a specific one.
      if (selectedCredId !== 'all' && row.credential_id !== selectedCredId) return;
      setSnaps((prev) => {
        // Skip duplicate (same id) — Supabase realtime can occasionally
        // re-deliver during reconnects.
        if (prev.some((s) => s.id === row.id)) return prev;
        return [...prev, row];
      });
    },
  );

  // Realtime subscribes to the trade_events table directly (Supabase realtime
  // works on tables, not views). Filter to match veritas.trade_fills:
  //   - event_kind = 0  (excludes chain_genesis and future compensating kinds)
  //   - deal_type IN (0, 1)  (BUY/SELL only — excludes DEAL_TYPE_BALANCE
  //     deposits/withdrawals/rebates and other non-fill deal types)
  // Without this, a deposit landing in real-time would briefly inflate the
  // trades list and KPIs until next reload.
  useRealtimeInserts<TradeRow & { credential_id: string; event_kind?: number }>(
    'veritas',
    'trade_events',
    realtimeFilter,
    (row) => {
      if (row.event_kind !== undefined && row.event_kind !== 0) return;
      if (row.deal_type !== 0 && row.deal_type !== 1) return;
      if (selectedCredId !== 'all' && row.credential_id !== selectedCredId) return;
      // Option 2: aggregates + journal window are server-sourced now; a new fill
      // triggers a refetch rather than mutating an in-memory full array.
      setRefreshTick((t) => t + 1);
    },
  );

  // Derived
  // P2: merge hourly (long history) + recent raw. Boundary = first recent raw
  // point, so the two never overlap and never leave a gap. Dormant accounts
  // (no recent raw) fall back to all-hourly.
  const equity   = useMemo(() => {
    const cutoffMs = snaps.length ? Date.parse(snaps[0].snapshot_ts) : Date.now();
    return buildEquity(hourlySnaps, snaps, cutoffMs);
  }, [hourlySnaps, snaps]);
  const ddSeries = useMemo(() => drawdownSeries(equity), [equity]);

  // Capital-flow aggregates. Used by the metadata strip ("Deposits +$10K")
  // and to drive the equity-curve reference markers below. We deliberately
  // categorise rebates/bonuses separately from deposits — the trader cares
  // about the distinction (rebates are earned through trading volume; a
  // deposit is injected capital that shouldn't count as trading edge).
  const balanceFlows = useMemo(() => {
    let deposits = 0;
    let withdrawals = 0;
    let rewards = 0;       // rebates + bonuses
    let other = 0;         // credits, charges, corrections, dividends, interest, tax
    for (const op of balanceOps) {
      if (op.category === 'deposit' || op.category === 'deposit_unclassified') {
        deposits += op.amount;
      } else if (op.category === 'withdrawal' || op.category === 'withdrawal_unclassified') {
        withdrawals += op.amount;       // negative
      } else if (op.category === 'rebate' || op.category === 'bonus') {
        rewards += op.amount;
      } else {
        other += op.amount;
      }
    }
    return {
      deposits, withdrawals, rewards, other,
      hasAny: balanceOps.length > 0,
      net: deposits + withdrawals + rewards + other,
    };
  }, [balanceOps]);

  // Subset for the equity-curve reference markers. We only mark deposits
  // and withdrawals — rebates/bonuses are typically small and would
  // clutter the chart. Rewards still appear in the metadata strip total.
  const capitalFlowMarkers = useMemo(
    () => balanceOps.filter((op) =>
      op.category === 'deposit' || op.category === 'withdrawal'
        || op.category === 'deposit_unclassified'
        || op.category === 'withdrawal_unclassified'
    ),
    [balanceOps],
  );

  // Risk-adjusted metrics — derived from the snapshot equity series. Account-
  // level (NOT scoped by Source/Magic), since these describe how the account
  // actually performed end-to-end. Snapshots aren't per-bot, so we can't
  // narrow these down without misrepresenting reality.
  const riskOverall = useMemo(
    () => riskMetrics(equity, balanceOps.map((o) => ({ t: o.snapshot_ts, amount: o.amount }))),
    [equity, balanceOps],
  );

  // Available magics for the dropdown — server-computed (credential-scoped,
  // magic-agnostic), sorted by trade count desc. From account_trade_analytics.
  const availableMagics = useMemo(() => tradeAgg?.magics ?? [], [tradeAgg]);

  // S8 — Per-bot performance rows. Server-computed (account_bot_breakdown):
  // all-magics, credential-scoped (this panel IS the bot picker). Net P&L is
  // cost-inclusive (positionNet) and realized DD mirrors veritas.drawdown_by_magic.
  // The decay flag stays client-side (descriptive, spec §4.3) over the 30d
  // window sums the RPC returns.
  const mapBotRow = useCallback(
    (b: BotBreakdownRow, override?: { name?: string; isSystem?: boolean }): BotPerfRow => {
      const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000; // 30 days ago
      const id = b.magic === 0 ? undefined : eaIdentities.get(b.magic);
      const name = override?.name ?? (b.magic === 0
        ? 'Manual'
        : (id?.name ?? `Unregistered (magic ${b.magic})`));
      const grossWin = Number(b.gross_win);
      const grossLoss = Number(b.gross_loss); // ≤ 0
      const netPnl = Number(b.net_pnl);
      const winPct = b.trades ? (b.wins / b.trades) * 100 : 0;
      const profitFactor = grossLoss < 0 ? grossWin / Math.abs(grossLoss) : null;
      const avgNet = b.trades ? netPnl / b.trades : 0;
      const lastCloseMs = b.last_close ? Date.parse(b.last_close) : null;
      const cold = lastCloseMs != null && lastCloseMs < cutoff;
      let flag: BotFlag;
      if (cold) {
        flag = 'cold';
      } else if (b.base_n >= 10 && b.trail_n >= 10) {
        const bAvg = Number(b.base_sum) / b.base_n; // prior period (before last 30d)
        const tAvg = Number(b.trail_sum) / b.trail_n; // last 30 days
        const decayed = (tAvg <= 0 && bAvg > 0) || (bAvg > 0 && tAvg < 0.5 * bAvg);
        flag = decayed ? 'decay' : 'ok';
      } else {
        flag = 'building';
      }
      return {
        magic: override?.isSystem ? -1 : b.magic,
        name,
        version: (override?.isSystem || b.magic === 0) ? null : (id?.version ?? null),
        trades: b.trades, netPnl, wins: b.wins, winPct, profitFactor,
        avgNet, best: Number(b.best), worst: Number(b.worst),
        realizedDdAbs: Number(b.realized_dd_abs), volume: Number(b.volume),
        lastClose: b.last_close,
        flag, trailingNet: Number(b.trail_sum), trailingTrades: b.trail_n,
        isSystem: override?.isSystem,
      };
    },
    [eaIdentities],
  );

  const botPerf = useMemo<BotPerfRow[]>(() => {
    const rows = botRows.map((b) => mapBotRow(b));
    rows.sort((x, y) => y.netPnl - x.netPnl);
    return rows;
  }, [botRows, mapBotRow]);

  // When the account is flagged "one system" (many TradeFlow magics that are
  // really one book), the Per-Bot panel folds all EA magics into a single
  // "TradeFlow (system)" row and keeps Manual (magic 0) separate. The system
  // row's realized DD is the combined-book account figure (serverDd) — summing
  // per-bot DDs would overstate it (path-dependent). `botPerf` above stays
  // per-magic so name resolution and the EA Benchmark panel are unaffected.
  const singleSystem = userMeta?.single_system ?? false;
  const displayBotRows = useMemo<BotPerfRow[]>(() => {
    if (!singleSystem) return botPerf;
    const ea = botRows.filter((b) => b.magic !== 0);
    const out: BotPerfRow[] = botRows.filter((b) => b.magic === 0).map((b) => mapBotRow(b));
    if (ea.length) {
      const sum = (f: (b: BotBreakdownRow) => number) => ea.reduce((s, b) => s + f(b), 0);
      const agg: BotBreakdownRow = {
        magic: -1,
        trades: sum((b) => Number(b.trades)),
        net_pnl: sum((b) => Number(b.net_pnl)),
        wins: sum((b) => Number(b.wins)),
        gross_win: sum((b) => Number(b.gross_win)),
        gross_loss: sum((b) => Number(b.gross_loss)),
        best: ea.reduce((m, b) => Math.max(m, Number(b.best)), -Infinity),
        worst: ea.reduce((m, b) => Math.min(m, Number(b.worst)), Infinity),
        volume: sum((b) => Number(b.volume)),
        last_close: ea.reduce<string | null>(
          (m, b) => (b.last_close && (!m || b.last_close > m) ? b.last_close : m), null),
        realized_dd_abs: 0,
        base_n: sum((b) => Number(b.base_n)),
        base_sum: sum((b) => Number(b.base_sum)),
        trail_n: sum((b) => Number(b.trail_n)),
        trail_sum: sum((b) => Number(b.trail_sum)),
      };
      const sysRow = mapBotRow(agg, { name: 'TradeFlow (system)', isSystem: true });
      sysRow.realizedDdAbs = serverDd?.realizedDdAbs ?? sum((b) => Number(b.realized_dd_abs));
      out.push(sysRow);
    }
    out.sort((x, y) => y.netPnl - x.netPnl);
    return out;
  }, [singleSystem, botPerf, botRows, mapBotRow, serverDd?.realizedDdAbs]);

  // magic → display name for panels that don't need the full BotPerfRow
  // (EA Benchmark). Derived from botPerf so identity resolution stays in
  // exactly one place.
  const botNameByMagic = useMemo(
    () => new Map(botPerf.map((r) => [r.magic, r.name])),
    [botPerf],
  );

  // KPIs + chart aggregates — server-computed (account_trade_analytics), magic-
  // scoped. kpisFromAnalytics folds in the equity-derived parts (return/maxDD)
  // and derives PF/win-rate/expectancy from primitives with the metrics.ts math.
  const kpis = useMemo(
    () => (tradeAgg ? kpisFromAnalytics(equity, tradeAgg.kpis) : EMPTY_KPIS),
    [equity, tradeAgg],
  );

  // Trading return: pure-trading P&L over user-declared starting capital.
  // Distinct from kpis.returnPct (which is equity-growth and conflates
  // trader skill with deposits/withdrawals). Returns null when no starting
  // capital is declared — the UI then falls back to labelling returnPct
  // as "Account return" so the meaning stays honest.
  const tradingReturnPct = useMemo<number | null>(() => {
    if (!startingCapital || startingCapital.asserted_balance <= 0) return null;
    return (kpis.netPnL / startingCapital.asserted_balance) * 100;
  }, [startingCapital, kpis.netPnL]);

  const daily      = useMemo(() => (tradeAgg ? dailyFromAnalytics(tradeAgg) : []), [tradeAgg]);
  const hourly     = useMemo(() => (tradeAgg ? hourlyFromAnalytics(tradeAgg) : []), [tradeAgg]);
  const histo      = useMemo(() => (tradeAgg ? histogramFromAnalytics(tradeAgg) : []), [tradeAgg]);
  const volBuckets = useMemo(() => (tradeAgg ? volumeBucketsFromAnalytics(tradeAgg) : []), [tradeAgg]);
  const source     = useMemo(
    () => (tradeAgg
      ? sourceFromAnalytics(tradeAgg)
      : { bot: { count: 0, profit: 0, winRate: 0, volume: 0 }, manual: { count: 0, profit: 0, winRate: 0, volume: 0 } }),
    [tradeAgg],
  );
  const rOverall   = useMemo(() => (posAgg ? rStatsFromAnalytics(posAgg) : EMPTY_RSTATS), [posAgg]);

  // Calendar / intraday / behavior — server-computed (account_position_analytics
  // + account_behavior_analytics), magic+source scoped.
  const calendarDayMap   = useMemo(() => (posAgg ? calendarMapFromAnalytics(posAgg) : new Map<string, DayBucket>()), [posAgg]);
  const intradayData     = useMemo(() => intradayFromAnalytics(posAgg), [posAgg]);
  const behaviorStats    = useMemo<TagStatsRow[]>(() => (behavior ? perTagFromBehavior(behavior) : []), [behavior]);
  const behaviorMistakes = useMemo<MistakeRow[]>(() => (behavior ? mistakesFromBehavior(behavior) : []), [behavior]);

  // Time-based (numeric) X-axis prep, x-domain/tick logic and the tooltip
  // label formatters moved into EquityDrawdownPanel (2026-07-15 merge) —
  // this page was their only consumer.

  // Equity Protector (per user+account, veritas.account_user_metadata).
  // Saved through its own partial-column RPC so label/category saves can
  // never clobber it (and vice versa).
  const equityProtector = useMemo(
    () =>
      userMeta?.equity_protector_mode && userMeta.equity_protector_value != null
        ? { mode: userMeta.equity_protector_mode, value: Number(userMeta.equity_protector_value) }
        : null,
    [userMeta],
  );

  const saveEquityProtector = useCallback(
    async (ep: { mode: 'pct' | 'abs'; value: number } | null) => {
      const r = await veritas.rpc('set_account_equity_protector', {
        p_account_login: accountLogin,
        p_mode:  ep?.mode  ?? null,
        p_value: ep?.value ?? null,
      });
      if (r.error) { setError(r.error.message); throw new Error(r.error.message); }
      setError(null);
      setUserMeta((m) => ({
        display_label: null, category: null, single_system: false,
        ...m,
        equity_protector_mode:  ep?.mode  ?? null,
        equity_protector_value: ep?.value ?? null,
      }));
    },
    [accountLogin],
  );

  if (loading) {
    return <div className="grid h-64 place-items-center text-text-dim">Loading account {accountLogin}…</div>;
  }
  if (error) {
    return (
      <div className="rounded border border-red/40 bg-red/10 px-3 py-2 text-sm text-red">
        {error}
      </div>
    );
  }
  if (tradeAgg && tradeAgg.magics.length === 0) {
    return (
      <div className="space-y-4">
        {credentials.length > 1 && (
          <CredentialFilter
            credentials={credentials}
            selected={selectedCredId}
            onSelect={setSelectedCredId}
          />
        )}
        <div className="grid h-64 place-items-center text-text-dim">
          No trade data for account {accountLogin}
          {selectedCredId !== 'all' ? ' on this credential' : ''} yet.
        </div>
      </div>
    );
  }

  // Headline metadata. P2: prefer the live recent-window tail (realtime keeps
  // it newest); fall back to the dedicated latest-raw row for dormant accounts
  // that have no snapshots inside the recent window.
  const latest = (snaps.length ? snaps[snaps.length - 1] : latestSnap) ?? undefined;
  // Trading-only Today P&L for the daily_loss alert: strip same-broker-day
  // deposits/withdrawals from the EA-supplied day_pnl. The view owns the
  // broker-day windowing (todayOpsByCred); we apply the trivial subtraction
  // against the live `latest` snapshot so the alert reacts to intraday moves.
  const dayPnlTrading = latest
    ? (latest.day_pnl ?? 0) - (todayOpsByCred.get(latest.credential_id) ?? 0)
    : null;
  const periodStart = equity[0]?.t ?? '';
  const periodEnd   = equity[equity.length - 1]?.t ?? '';

  return (
    <div className="space-y-3.5">
      {/* ── Read-only viewer banner ────────────────────────────────
           Shared accounts (account_grants, 06-04) render every edit
           affordance disabled — without this banner the viewer just sees
           dead buttons with no explanation. canEdit is UX gating only;
           RLS is the real boundary. user is checked so the banner can't
           flash during auth bootstrap. */}
      {user && !canEdit && (
        <div className="flex flex-wrap items-center gap-2.5 rounded-md border border-blue/25 bg-blue/[0.07] px-3.5 py-2.5 text-[12.5px] text-text-mid">
          <span className="rounded-full border border-blue/30 bg-blue/10 px-2.5 py-0.5 text-[9.5px] font-bold uppercase tracking-eyebrow text-blue">
            🔒 read-only
          </span>
          <span>
            This account is <b className="font-medium text-text">shared with you</b> — raw journal only.
            You can view trades, equity and metrics; labels, starting capital, notes, tags,
            alerts and theses are owner-only.
          </span>
        </div>
      )}
      {/* ── Header ─────────────────────────────────────────────────── */}
      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <Link to="/dashboard" className="mb-1 inline-block text-[10px] uppercase tracking-eyebrow text-text-dim hover:text-green">
            ← All Accounts
          </Link>
          {/* Brand mark intentionally omitted here — it already lives in the
              global nav header; repeating it in the account detail is noise. */}
          <div className="mt-1 flex items-center gap-3">
            <LivePill>LIVE PERFORMANCE</LivePill>
            <ChainIntegrityPill accountLogin={accountLogin} />
            <ReconciliationPill accountLogin={accountLogin} />
          </div>
          <AccountTitleEditor
            accountLogin={accountLogin}
            value={userMeta}
            knownCategories={userCategories}
            readOnly={!canEdit}
            onChange={(next, allCats) => {
              // MERGE, don't replace — `next` carries only the editor's own
              // fields; a label save must not wipe the equity-protector
              // fields from local state (the DB is safe either way: EP has
              // its own partial-column RPC).
              setUserMeta((m) => ({
                equity_protector_mode:  m?.equity_protector_mode  ?? null,
                equity_protector_value: m?.equity_protector_value ?? null,
                ...next,
              }));
              if (allCats) setUserCategories(allCats);
            }}
            onError={setError}
          />
          <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-3">
            {credentials.length > 1 && (
              <CredentialFilter
                credentials={credentials}
                selected={selectedCredId}
                onSelect={setSelectedCredId}
              />
            )}
            {/* Source filter — scopes the journal + behavior panels.
                For traders running mixed bot+manual setups, picking
                "Manual" focuses tagging and reflection on their own
                decisions; bot trades are deterministic and don't
                deserve discipline tags. */}
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-eyebrow text-text-dim">
              <span>Source</span>
              <Chips>
                <Chip active={selectedSource === 'all'}    onClick={() => setSelectedSource('all')}>All</Chip>
                <Chip active={selectedSource === 'manual'} onClick={() => setSelectedSource('manual')}>Manual</Chip>
                <Chip active={selectedSource === 'bot'} alt onClick={() => setSelectedSource('bot')}>Bot</Chip>
              </Chips>
            </div>
            {/* Magic filter — pick a single bot's magic number to scope the
                ENTIRE trade-derived view (KPIs, charts, journal, behavior)
                so you can answer "how is bot X performing here?". Shown
                whenever there's at least one magic, even on single-bot
                accounts — picking "All magics" vs the sole bot is
                redundant but the dropdown signals that the feature is
                available. Hidden only when the account has no trades
                yet (nothing to filter). */}
            {availableMagics.length > 0 && (
              <MagicFilter
                magics={availableMagics}
                selected={selectedMagic}
                onSelect={setSelectedMagic}
              />
            )}
          </div>
          <div className="mt-3 flex flex-wrap gap-[18px] text-[11px] text-text-mid">
            <span>Period <b className="font-medium text-text">{periodStart.slice(0, 10)} → {periodEnd.slice(0, 10)}</b></span>
            {/* "Trades" intentionally dropped — the Total Trades KPI card and the
                Magic filter both already show the count; three copies is noise. */}
            <span>Broker <b className="font-medium text-text">{latest?.broker ?? '—'}</b></span>
            <span>EA <b className="font-medium text-text">{latest?.ea_version ?? '—'}</b></span>
            <span>Currency <b className="font-medium text-text">{latest?.currency ?? '—'}</b></span>
            {/* Capital flows — surfaced here so a deposit/withdrawal isn't read
                as trading P&L. Each line only renders when its bucket is
                non-zero, so accounts with no balance ops are unaffected. */}
            {balanceFlows.deposits > 0 && (
              <span>
                Deposits{' '}
                <b className="font-medium text-green">
                  +{fmtMoney(balanceFlows.deposits, '')}
                </b>
              </span>
            )}
            {balanceFlows.withdrawals < 0 && (
              <span>
                Withdrawals{' '}
                <b className="font-medium text-red">
                  −{fmtMoney(Math.abs(balanceFlows.withdrawals), '')}
                </b>
              </span>
            )}
            {balanceFlows.rewards > 0 && (
              <span>
                Rebates{' '}
                <b className="font-medium text-green">
                  +{fmtMoney(balanceFlows.rewards, '')}
                </b>
              </span>
            )}
            {/* Non-withdrawable promo / bonus capital (MT5/MT4 ACCOUNT_CREDIT).
                Surfaced beside the capital-flows so the trader sees what's
                actually theirs vs broker-granted credit that can be revoked.
                Only renders when the latest snapshot reports non-zero credit
                — pre-bridge-v1.04 rows have NULL credit and skip this pill.
                PostgREST returns numeric columns as strings, so Number() the
                value before comparing / formatting (fmtMoney's Number.isFinite
                guard rejects raw strings and returns "—"). */}
            {latest?.credit != null && Number(latest.credit) !== 0 && (
              <span title="Non-withdrawable promo / bonus capital granted by the broker. Counts toward equity (you can trade against it) but the broker can claw it back at any time. Floating P&L below has this removed.">
                Credit{' '}
                <b className="font-medium text-amber">
                  +{fmtMoney(Number(latest.credit), latest.currency ?? '')}
                </b>
              </span>
            )}
            {/* Starting capital — user-asserted baseline for what the account
                had when Veritas first connected. Click to declare/edit. */}
            <StartingCapitalEditor
              accountLogin={accountLogin}
              value={startingCapital}
              readOnly={!canEdit}
              firstObservedBalance={
                hourlySnaps[0]?.balance_open != null ? Number(hourlySnaps[0].balance_open)
                  : (snaps[0]?.balance ?? null)
              }
              firstObservedDate={
                hourlySnaps[0]?.hour_start?.slice(0, 10)
                  ?? snaps[0]?.snapshot_ts?.slice(0, 10) ?? null
              }
              onChange={(next) => setStartingCapital(next)}
              onError={setError}
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canEdit && (
            <button
              onClick={() => setShareOpen(true)}
              className="inline-flex items-center gap-2 rounded-md border border-line bg-bg-2 px-3 py-2 text-[10.5px] font-bold uppercase tracking-eyebrow text-text-mid transition-colors hover:border-green hover:text-text"
            >
              Share
            </button>
          )}
          <button
            onClick={() => setExportOpen(true)}
            className="inline-flex items-center gap-2 rounded-md border border-line bg-bg-2 px-3 py-2 text-[10.5px] font-bold uppercase tracking-eyebrow text-text-mid transition-colors hover:border-green hover:text-text"
          >
            Export
          </button>
        </div>
      </header>

      {/* ── KPI grid (7 cards — added Expectancy R) ───────────────── */}
      <section className="mb-[18px] grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-7">
        <Kpi
          label="Net P&L"
          tone={kpis.netPnL >= 0 ? 'green' : 'red'}
          value={
            <span className={kpis.netPnL >= 0 ? 'text-green' : 'text-red'}>
              {kpis.netPnL >= 0 ? '+' : ''}{fmtMoney(kpis.netPnL, '')}
            </span>
          }
          sub={
            // When the user has declared starting capital, show both Trading
            // and Account return so the trader can see the gap between
            // pure-trading edge and total equity growth (deposits inflate
            // Account return; Trading return ignores them).
            // When no starting capital is declared, label the existing
            // returnPct as "Account return" so its meaning is unambiguous.
            // Lead with Trading return (deposits excluded — the real edge).
            // The raw equity-growth figure includes deposits/withdrawals, so it
            // is NOT performance: render it dimmed and explicitly labelled
            // "incl. deposits" so a $20k deposit can't read as a +94% return.
            tradingReturnPct !== null ? (
              <span title="Trading return = P&L from BUY/SELL fills ÷ your declared starting capital, deposits/withdrawals excluded — this is your actual edge. The dimmed figure is raw equity growth from the first observed balance and INCLUDES capital flows, so it is not performance.">
                <span className={tradingReturnPct >= 0 ? 'text-green' : 'text-red'}>
                  Trading {tradingReturnPct >= 0 ? '+' : ''}{fmtPct(tradingReturnPct)}
                </span>
                <span className="text-text-dim">
                  {' · '}{kpis.returnPct >= 0 ? '+' : ''}{fmtPct(kpis.returnPct)} incl. deposits
                </span>
              </span>
            ) : balanceFlows.net !== 0 ? (
              <span
                className="text-text-dim"
                title="Raw equity growth from the first observed balance — INCLUDES deposits/withdrawals, so it is not trading performance. Declare starting capital in the header to see a deposits-excluded Trading return."
              >
                {kpis.returnPct >= 0 ? '+' : ''}{fmtPct(kpis.returnPct)} incl. deposits
              </span>
            ) : (
              <span
                className={kpis.returnPct >= 0 ? 'text-green' : 'text-red'}
                title="Account return: equity growth from the first observed balance. No deposits or withdrawals on this account, so this equals trading return."
              >
                {kpis.returnPct >= 0 ? '+' : ''}{fmtPct(kpis.returnPct)} account return
              </span>
            )
          }
        />
        <Kpi
          label="Win Rate"
          value={fmtPct(kpis.winRate)}
          sub={`${kpis.winners}W · ${kpis.losers}L`}
        />
        <Kpi
          label="Profit Factor"
          tone="blue"
          value={Number.isFinite(kpis.profitFactor) ? kpis.profitFactor.toFixed(2) : '∞'}
          sub={`Expectancy ${fmtMoney(kpis.expectancy, '')}`}
        />
        <Kpi
          label="Total Trades"
          tone="blue"
          value={kpis.totalTrades}
          sub={`${kpis.closedPositions} closed positions · ${kpis.totalVolume.toFixed(2)} lots`}
        />
        <Kpi
          label="Max Drawdown"
          tone={(serverDd ? serverDd.maxDdPct : kpis.maxDdPct) > 5 ? 'red' : 'amber'}
          value={
            <span
              className="text-red"
              title={
                serverDd
                  ? `Equity / total drawdown — worst peak-to-trough of balance + open positions, deposits/withdrawals removed: ${fmtPct(serverDd.maxDdPct)} (${fmtMoney(serverDd.maxDdAbs, '')}).\nRealized (closed trades only): ${fmtPct(serverDd.realizedDdPct)} (${fmtMoney(serverDd.realizedDdAbs, '')}).\nPeak floating (open positions underwater at one instant): ${fmtPct(serverDd.floatingDdPct)} (${fmtMoney(serverDd.floatingDdAbs, '')}).`
                  : 'Computing deposit-adjusted figures…'
              }
            >
              −{fmtPct(serverDd ? serverDd.maxDdPct : kpis.maxDdPct)}
            </span>
          }
          sub={
            serverDd ? (
              <span title="Realized = balance / closed-trade drawdown. Floating = peak unrealized loss on open positions. Headline above is equity/total (the worst of balance + open positions).">
                Realized {fmtPct(serverDd.realizedDdPct)}
                <span className="text-text-dim"> · </span>
                Floating {fmtPct(serverDd.floatingDdPct)}
              </span>
            ) : (
              // Client estimate while the server RPC is in flight — balance-only,
              // so it understates the real (equity-inclusive) drawdown. Say so
              // instead of presenting it as the number.
              <span className="text-amber" title="Client estimate from the hourly balance series — floating losses invisible, real drawdown was deeper. Server figure (deposit-adjusted, equity-inclusive) loading…">
                estimate — understates DD · server figure loading…
              </span>
            )
          }
        />
        <Kpi
          label="Streaks"
          value={
            <span>
              <span className="text-green">{kpis.longestWinStreak}W</span>
              <span className="text-text-dim"> / </span>
              <span className="text-red">{kpis.longestLossStreak}L</span>
            </span>
          }
          sub={`Avg win ${fmtMoney(kpis.avgWin, '')} · Avg loss ${fmtMoney(kpis.avgLoss, '')}`}
        />
        <Kpi
          label="Expectancy R"
          tone={
            rOverall.expectancyR == null ? 'blue'
            : rOverall.expectancyR > 0 ? 'green'
            : rOverall.expectancyR < 0 ? 'red'
            : 'blue'
          }
          value={
            rOverall.expectancyR == null ? '—'
            : fmtRMultiple(rOverall.expectancyR)
          }
          sub={
            rOverall.withR === 0
              ? 'No SL data'
              : `${rOverall.withR}/${rOverall.count} positions w/ SL`
          }
        />
      </section>

      {/* ── Equity + Drawdown ──────────────────────────────────────────
           Merged full-width panel (2026-07-15) with a runtime stacked /
           overlay toggle. DD basis contract (server vs estimated badge)
           lives inside the component — never show estimated data unlabeled
           (same truth-defect class as L35). */}
      <EquityDrawdownPanel
        equity={equity}
        ddClient={ddSeries}
        ddServer={ddSeriesServer}
        capitalFlowMarkers={capitalFlowMarkers}
        equityProtector={equityProtector}
        onSaveEquityProtector={canEdit ? saveEquityProtector : undefined}
      />

      {/* ── Risk-adjusted metrics ──────────────────────────────────── */}
      <RiskMetricsPanel metrics={riskOverall} />

      {/* ── Daily / Win-Loss / Hourly ──────────────────────────────── */}
      <div className="grid gap-3.5 lg:grid-cols-3">
        <Panel>
          <PanelHead title="Daily P&L" />
          <PanelBody>
            <div className="h-[200px] sm:h-[230px] md:h-[260px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={daily}>
                  <CartesianGrid stroke={GRID} vertical={false} />
                  <XAxis dataKey="date" tickFormatter={(v) => v.slice(5)} tick={AXIS} />
                  <YAxis tick={AXIS} width={50} />
                  <Tooltip contentStyle={TooltipStyle} formatter={(v: number) => fmtMoney(v, '')} />
                  <Bar dataKey="profit" radius={[2, 2, 0, 0]}>
                    {daily.map((d, i) => (
                      <Cell key={i} fill={d.profit >= 0 ? '#00ff88' : '#ef4444'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </PanelBody>
        </Panel>

        <Panel>
          <PanelHead title="Win / Loss Distribution" />
          <PanelBody>
            <div className="h-[200px] sm:h-[230px] md:h-[260px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={histo}>
                  <CartesianGrid stroke={GRID} vertical={false} />
                  <XAxis dataKey="midpoint" tick={AXIS} tickFormatter={(v) => v.toFixed(0)} />
                  <YAxis tick={AXIS} width={36} allowDecimals={false} />
                  <Tooltip
                    contentStyle={TooltipStyle}
                    formatter={(v, _n, item) => {
                      const label = (item as { payload?: { label?: string } })?.payload?.label ?? '';
                      return [`${v} trades`, label];
                    }}
                  />
                  <Bar dataKey="count" radius={[2, 2, 0, 0]}>
                    {histo.map((b, i) => (
                      <Cell key={i} fill={b.positive ? '#00ff88' : '#ef4444'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </PanelBody>
        </Panel>

        <Panel>
          <PanelHead title={<>Profit by Hour <span className="text-text-dim">UTC</span></>} />
          <PanelBody>
            <div className="h-[200px] sm:h-[230px] md:h-[260px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={hourly}>
                  <CartesianGrid stroke={GRID} vertical={false} />
                  <XAxis dataKey="hour" tick={AXIS} tickFormatter={(v) => `${v}h`} />
                  <YAxis tick={AXIS} width={50} />
                  <Tooltip contentStyle={TooltipStyle} formatter={(v: number) => fmtMoney(v, '')} />
                  <Bar dataKey="profit" radius={[2, 2, 0, 0]}>
                    {hourly.map((h, i) => (
                      <Cell key={i} fill={h.profit >= 0 ? '#3b82f6' : '#ef4444'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </PanelBody>
        </Panel>
      </div>

      {/* ── Position size + Bot vs Manual ──────────────────────────── */}
      <div className="grid gap-3.5 lg:grid-cols-[3fr_2fr]">
        <Panel>
          <PanelHead title="Position-Size Distribution" />
          <PanelBody>
            <div className="flex flex-col gap-2">
              {volBuckets.map((b) => (
                <DistRow
                  key={b.label}
                  label={b.label}
                  value={`${b.count}  ·  ${fmtMoney(b.profit, '')}`}
                  ratio={b.countShare}
                  positive={b.profit >= 0}
                />
              ))}
            </div>
          </PanelBody>
        </Panel>

        <Panel>
          <PanelHead title="Bot vs Manual Activity" />
          <PanelBody>
            <div className="grid grid-cols-2 gap-3">
              <SourceCard kind="bot"    seg={source.bot}    total={kpis.totalTrades} />
              <SourceCard kind="manual" seg={source.manual} total={kpis.totalTrades} />
            </div>
          </PanelBody>
        </Panel>
      </div>

      {/* ── Per-Bot Performance (S8) ───────────────────────────────
           One row per magic, derived from the full credential-scoped
           closedPositions set. Clicking a row scopes the whole page to
           that bot via the existing magic filter. Descriptive decay
           signal (L4) — surfaced, never prescribed. */}
      <PerBotPanel
        rows={displayBotRows}
        selectedMagic={selectedMagic}
        onSelect={setSelectedMagic}
        collapsed={singleSystem}
      />

      {/* ── EA Benchmark (advertised vs realized) ───────────────────
           Surfaces veritas.ea_benchmark / ea_promise (2026-05-22 engine,
           first UI). Sits directly under the Per-Bot panel — same per-magic
           grain, but answers "is the vendor's claim holding?" instead of
           "how is the bot doing?". Descriptive divergence only (findings
           framing decision): AHEAD/BEHIND/BREACHED/NO CLAIM, never a
           "verified" stamp. Renders nothing when the account has no bot
           positions; "no claim on file" + admin-only inline claim entry is
           the expected state until ea_promise is populated. */}
      <EaBenchmarkPanel
        accountLogin={accountLogin}
        botNames={botNameByMagic}
      />

      {/* ── Calendar heatmap + cumulative-through-day ───────────────
           Server-computed (account_position_analytics), magic+source scoped
           so the Source filter (All/Manual/Bot) scopes them too. */}
      <div className="grid gap-3.5 lg:grid-cols-[3fr_2fr]">
        <CalendarHeatmap dayMap={calendarDayMap} months={3} />
        <CumulativeIntradayPanel data={intradayData} />
      </div>

      {/* ── Alerts (owner/admin only — viewers have no alert config) ── */}
      {canEdit && (
      <AlertsPanel
        rules={alertRules}
        latestSnapshot={latest}
        dayPnlTrading={dayPnlTrading}
        // Adding a rule needs a concrete credential; if the user has 'all'
        // selected we default to the most recently-created credential.
        defaultCredentialId={
          selectedCredId !== 'all'
            ? selectedCredId
            : credentials[credentials.length - 1]?.id ?? null
        }
        credentials={credentials}
        onChanged={(rule) => {
          setAlertRules((prev) => {
            const idx = prev.findIndex((r) => r.id === rule.id);
            if (idx === -1) return [...prev, rule];
            const next = [...prev]; next[idx] = rule; return next;
          });
        }}
        onDeleted={(id) => setAlertRules((prev) => prev.filter((r) => r.id !== id))}
        onError={setError}
      />
      )}

      {/* ── Behavior analytics (tag + mistake correlation) ──────────
           Scoped to whatever the source filter picks — by default 'all',
           but a trader who only tags manual trades will want 'manual' so
           the untagged baseline isn't contaminated by deterministic bots. */}
      <BehaviorPanels
        stats={behaviorStats}
        mistakes={behaviorMistakes}
        scopeLabel={selectedSource}
      />

      {/* ── Thesis panel (S2 M1) — descriptive surface, opt-in per account.
            Sits between Behavior (Tag performance / Mistakes) and the
            Trade Journal because that's the natural read order: see your
            patterns, then see the structured contracts you wrote against
            them, then drop into the deal-by-deal log. Collapsible — the
            user's choice persists per account in localStorage. */}
      {/* Thesis is owner-only analysis (viewers can't read it via RLS), so the
          whole panel is hidden for shared read-only viewers. */}
      {canEdit && (
        <section className="mb-[18px]">
          <ThesisPanel accountLogin={accountLogin} />
        </section>
      )}

      {/* ── Trade journal (tabbed: deals vs closed positions) ─────── */}
      {/* Empty-intersection guard: "Source: Manual" + a specific bot magic can
          never match (and vice versa). A silent empty table reads as missing
          data — name the conflict instead. Only fires when the magic filter
          alone DOES have trades, so a genuinely empty account isn't nagged. */}
      {dealCount === 0 && posCount === 0 && selectedSource !== 'all' && selectedMagic !== 'all' && (tradeAgg?.magics.length ?? 0) > 0 && (
        <div className="flex items-start gap-2.5 rounded-md border border-amber/30 bg-amber/[0.07] px-3.5 py-2.5 text-[12.5px] text-amber">
          <span aria-hidden>⚠</span>
          <span>
            No trades match <b className="font-semibold">Source: {selectedSource}</b> combined with the
            selected bot filter — these two exclude each other. Clear one of them to see trades again.
          </span>
        </div>
      )}
      <TradeJournal
        readOnly={!canEdit}
        trades={trades}
        positions={closedPositions}
        dealCount={dealCount}
        posCount={posCount}
        loadingMore={journalLoading}
        onLoadMore={() => setJournalLimit((l) => l + 100)}
        notes={notes}
        tags={tags}
        positionTags={positionTagsMap}
        onOpenNote={(p) => setOpenNote(p)}
        onOpenTags={(p) => setOpenTags(p)}
      />

      {/* ── Modals ─────────────────────────────────────────────────── */}
      {openNote && (
        <TradeNoteDrawer
          open={!!openNote}
          position={openNote}
          existingNote={notes.find(
            (n) => n.account_login === openNote.account_login
              && n.position_ticket === openNote.position_ticket,
          )}
          onClose={() => setOpenNote(null)}
          onSaved={(saved) => {
            setNotes((prev) => {
              const idx = prev.findIndex(
                (n) => n.account_login === saved.account_login
                  && n.position_ticket === saved.position_ticket,
              );
              if (idx === -1) return [...prev, saved];
              const next = [...prev]; next[idx] = saved; return next;
            });
          }}
        />
      )}
      {openTags && (
        <TagPicker
          open={!!openTags}
          position={openTags}
          selected={positionTagsMap.get(openTags.position_ticket) ?? new Set()}
          allTags={tags}
          onClose={() => setOpenTags(null)}
          onChange={(next) => {
            // Rebuild trade_tags state for this position from `next`
            const others = tradeTags.filter(
              (tt) => tt.position_ticket !== openTags.position_ticket,
            );
            const now = new Date().toISOString();
            const mine: TradeTag[] = [...next].map((tag_id) => ({
              account_login:   openTags.account_login,
              position_ticket: openTags.position_ticket,
              tag_id,
              applied_at: now,
              applied_by: null,
            }));
            setTradeTags([...others, ...mine]);
          }}
          onCreatePersonalTag={async (name) => {
            const session = (await supabase.auth.getSession()).data.session;
            if (!session) return null;
            const r = await veritas
              .from('tags')
              .insert({ name, owner_user_id: session.user.id, color: '60a5fa' })
              .select()
              .maybeSingle();
            if (r.error || !r.data) { setError(r.error?.message ?? 'Failed to create tag'); return null; }
            const created = r.data as Tag;
            setTags((prev) => [...prev, created]);
            return created;
          }}
        />
      )}

      {/* Export dialog — opens when the EXPORT button in the header is clicked.
          Receives full unfiltered data; the dialog applies the date range filter
          before generating outputs. */}
      <ExportDialog
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        accountLogin={accountLogin}
        ownerEmail={null}
        broker={latest?.broker ?? null}
        eaVersion={latest?.ea_version ?? null}
        credentialId={selectedCredId === 'all' ? null : selectedCredId}
        positionTags={positionTagsMap}
        tags={tags}
      />

      <ShareDialog
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        accountLogin={accountLogin}
      />
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Chain-integrity pill. On mount it runs TWO checks and renders a status pill
// next to LIVE PERFORMANCE:
//   1. veritas.verify_chain(account_login) — CONSISTENCY: no chained row has
//      been silently UPDATEd / DELETEd / INSERTed-in-the-middle.
//   2. count of event_kind=0 rows with seq IS NULL — COMPLETENESS: trade
//      events that exist but were never chained. verify_chain is blind to
//      these — it only walks chained rows — which is exactly how the
//      2026-05-07 ingest regression froze 9/11 chains behind a green badge.
//      "Chain OK" now requires BOTH: internally consistent AND complete.
//
// What verify_chain does NOT prove: that the chain hasn't been rewritten
// end-to-end by someone with full Postgres access. That needs an external
// anchor (signed daily Merkle root / OpenTimestamps), deliberately not in v1
// (tasks/s1-plan.md D4) — so the language stays narrow.
//
// EMPTY status (account not yet chained) and loading render nothing — no
// point surfacing absence, and no flicker.
function ChainIntegrityPill({ accountLogin }: { accountLogin: number }) {
  type Verdict = 'loading' | 'ok' | 'broken' | 'empty' | 'error';
  const [status, setStatus] = useState<Verdict>('loading');
  const [detail, setDetail] = useState<string>('');
  // Count of unchained event_kind=0 rows. null = not yet known / check failed.
  const [unchained, setUnchained] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function check() {
      const [chainRes, unchainedRes] = await Promise.all([
        veritas.rpc('verify_chain', { p_account_login: accountLogin }),
        veritas
          .from('trade_events')
          .select('*', { count: 'exact', head: true })
          .eq('account_login', accountLogin)
          .eq('event_kind', 0)
          .is('seq', null),
      ]);
      if (cancelled) return;

      // Completeness — defensive: a failed count must not break the pill.
      setUnchained(unchainedRes.error ? null : (unchainedRes.count ?? 0));

      // Consistency.
      if (chainRes.error) {
        setStatus('error');
        setDetail(chainRes.error.message);
        return;
      }
      const row = (Array.isArray(chainRes.data) ? chainRes.data[0] : chainRes.data) as
        | { status: string; detail?: string | null }
        | undefined;
      if (!row) { setStatus('empty'); return; }
      if (row.status === 'OK') {
        setStatus('ok');
        setDetail(row.detail ?? 'verified');
      } else if (row.status === 'EMPTY') {
        setStatus('empty');
      } else {
        // CHAIN_BROKEN | PREV_MISMATCH | (any future failure status)
        setStatus('broken');
        setDetail(`${row.status}: ${row.detail ?? ''}`);
      }
    }
    check();
    return () => { cancelled = true; };
  }, [accountLogin]);

  if (status === 'loading' || status === 'empty') return null;

  if (status === 'broken') {
    return (
      <span
        className="inline-flex items-center gap-1.5 rounded-full bg-red/10 px-2.5 py-[5px] text-[10px] font-bold uppercase tracking-eyebrow text-red"
        title={detail}
      >
        ⚠ Chain Tampered
      </span>
    );
  }

  if (status === 'error') {
    // RPC call itself failed (network, permission). Distinct from CHAIN_BROKEN.
    return (
      <span
        className="inline-flex items-center gap-1.5 rounded-full bg-amber/10 px-2.5 py-[5px] text-[10px] font-bold uppercase tracking-eyebrow text-amber"
        title={detail}
      >
        Chain ?
      </span>
    );
  }

  // status === 'ok' — chain is internally consistent. Completeness gate: if
  // trade events exist that were never chained, the chain is consistent but
  // INCOMPLETE — verify_chain can't see them, so it would otherwise read OK.
  if (unchained && unchained > 0) {
    return (
      <span
        className="inline-flex items-center gap-1.5 rounded-full bg-red/10 px-2.5 py-[5px] text-[10px] font-bold uppercase tracking-eyebrow text-red"
        title={`${unchained} trade event(s) are not in the hash chain — ingested by a path that bypassed the chained write. verify_chain only validates chained rows, so it still reads OK. Repair with backfill_chain_for_account.`}
      >
        ⚠ Chain Incomplete ({unchained})
      </span>
    );
  }

  // OK — consistent AND complete.
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full bg-green/10 px-2.5 py-[5px] text-[10px] font-bold uppercase tracking-eyebrow text-green"
      title={detail}
    >
      Chain OK
    </span>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Reconciliation pill. Reads the latest veritas.reconciliation_checks row for
// this account and renders a status pill next to the chain pill.
//
// What this proves — and the chain pill deliberately does NOT: that Veritas's
// event log is COMPLETE. The hash chain proves the chain is internally
// consistent; it cannot detect a deal that was never ingested in the first
// place. This pill compares the broker-reported balance (account_snapshots —
// an independent feed) against the balance Veritas's events derive. A non-zero
// delta means a trade event was dropped or duplicated on the way in. Populated
// daily by the veritas-reconcile Edge Function.
//
// No row yet / NO_DATA / loading → renders nothing (no flicker, no noise) —
// same posture as the chain pill's EMPTY/loading states.
function ReconciliationPill({ accountLogin }: { accountLogin: number }) {
  type Row = { status: string; delta: number | string | null; checked_at: string };
  const [row, setRow] = useState<Row | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const { data } = await veritas
        .from('reconciliation_checks')
        .select('status, delta, checked_at')
        .eq('account_login', accountLogin)
        .order('checked_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (cancelled) return;
      setRow((data as Row | null) ?? null);
      setLoaded(true);
    }
    load();
    return () => { cancelled = true; };
  }, [accountLogin]);

  if (!loaded || !row || row.status === 'NO_DATA') return null;

  // PostgREST can return numeric columns as strings — coerce defensively.
  const delta = Number(row.delta ?? 0);
  const checkedOn = new Date(row.checked_at).toLocaleDateString();

  if (row.status === 'DRIFT') {
    return (
      <span
        className="inline-flex items-center gap-1.5 rounded-full bg-red/10 px-2.5 py-[5px] text-[10px] font-bold uppercase tracking-eyebrow text-red"
        title={`Broker balance and Veritas's event log differ by ${delta >= 0 ? '+' : ''}${delta.toFixed(2)} — a trade event was dropped or duplicated on ingest. The hash chain can't catch this. Last checked ${checkedOn}.`}
      >
        ⚠ Drift {delta >= 0 ? '+' : ''}{delta.toFixed(2)}
      </span>
    );
  }

  // RECONCILED
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full bg-green/10 px-2.5 py-[5px] text-[10px] font-bold uppercase tracking-eyebrow text-green"
      title={`Broker balance matches Veritas's event log to the cent. Last checked ${checkedOn}.`}
    >
      Reconciled
    </span>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Starting-capital editor. Inline widget in the header metadata strip.
//
// What this is: a USER-ASSERTED claim about how much capital the account
// had on the day Veritas first connected to it. Used as the baseline for
// "trading return" calculations that need to exclude pre-bridge funding
// (which Veritas can't see — the bridge only forwards events from when it
// starts running, and the account's opening balance reflects whatever
// deposits + trading P&L happened before that).
//
// What this is NOT: a chain-attested fact. The label "declared" makes that
// explicit so the trader doesn't conflate it with the broker-attested
// trade history. If the user wants to share an audited track record with a
// coach or prop firm, this number is self-reported, not provable.
//
// Pre-fill heuristic: when the user opens the editor for the first time,
// we suggest (firstObservedBalance, firstObservedDate) — i.e. the balance
// in the very first snapshot Veritas saw. That's the most accurate guess
// we can make without additional data, and the user can override it.
function StartingCapitalEditor({
  accountLogin,
  value,
  firstObservedBalance,
  firstObservedDate,
  readOnly,
  onChange,
  onError,
}: {
  accountLogin: number;
  value: StartingCapitalRow | null;
  firstObservedBalance: number | null;
  firstObservedDate: string | null;
  readOnly?: boolean;
  onChange: (next: StartingCapitalRow | null) => void;
  onError: (msg: string) => void;
}) {
  const [editing, setEditing]   = useState(false);
  const [balance, setBalance]   = useState<string>(
    value ? String(value.asserted_balance) : (firstObservedBalance != null ? String(firstObservedBalance) : ''),
  );
  const [date, setDate]         = useState<string>(
    value?.as_of_date ?? firstObservedDate ?? '',
  );
  const [saving, setSaving]     = useState(false);

  // Re-sync when the parent prop changes (e.g. switching credential filter)
  useEffect(() => {
    setBalance(value ? String(value.asserted_balance) : (firstObservedBalance != null ? String(firstObservedBalance) : ''));
    setDate(value?.as_of_date ?? firstObservedDate ?? '');
  }, [value, firstObservedBalance, firstObservedDate]);

  async function save() {
    const parsed = Number(balance);
    if (!Number.isFinite(parsed) || parsed < 0) {
      onError('Starting capital must be a non-negative number');
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      onError('As-of date must be YYYY-MM-DD');
      return;
    }
    setSaving(true);
    const session = (await supabase.auth.getSession()).data.session;
    const userId  = session?.user.id ?? null;
    const r = await veritas
      .from('account_starting_capital')
      .upsert({
        account_login:    accountLogin,
        asserted_balance: parsed,
        as_of_date:       date,
        // updated_by always; created_by only on initial insert (DB
        // doesn't enforce, but we set it client-side so the audit trail
        // is meaningful when set).
        created_by: value ? value.created_by : userId,
        updated_by: userId,
      }, { onConflict: 'account_login' })
      .select()
      .maybeSingle();
    setSaving(false);
    if (r.error || !r.data) {
      onError(r.error?.message ?? 'Failed to save starting capital');
      return;
    }
    onChange(r.data as StartingCapitalRow);
    setEditing(false);
  }

  async function clear() {
    setSaving(true);
    const r = await veritas
      .from('account_starting_capital')
      .delete()
      .eq('account_login', accountLogin);
    setSaving(false);
    if (r.error) { onError(r.error.message); return; }
    onChange(null);
    setEditing(false);
  }

  // Read-only viewers: show the declared value as static text (or nothing if the
  // owner never declared one). No edit affordance, no "+ Declare" prompt.
  if (readOnly) {
    if (!value) return null;
    return (
      <span title={`User-declared starting balance as of ${value.as_of_date}. Used as the baseline for trading-return calcs. Not chain-attested.`}>
        Starting capital{' '}
        <b className="font-medium text-text">{fmtMoney(value.asserted_balance, '')}</b>
        <span className="ml-1 text-[10px] uppercase tracking-eyebrow text-text-dim">
          · {value.as_of_date} declared
        </span>
      </span>
    );
  }

  if (editing) {
    return (
      <span className="inline-flex items-center gap-2 rounded border border-line bg-bg-2 px-2 py-1">
        <span className="text-[10px] uppercase tracking-eyebrow text-text-dim">Starting capital</span>
        <input
          type="number"
          step="0.01"
          min="0"
          value={balance}
          onChange={(e) => setBalance(e.target.value)}
          className="w-24 rounded border border-line bg-bg px-1.5 py-0.5 text-[11px] text-text"
          placeholder="amount"
          disabled={saving}
        />
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="rounded border border-line bg-bg px-1.5 py-0.5 text-[11px] text-text"
          disabled={saving}
        />
        <button
          onClick={save}
          disabled={saving}
          className="rounded bg-green/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-eyebrow text-green hover:bg-green/30 disabled:opacity-50"
        >
          {saving ? '…' : 'Save'}
        </button>
        <button
          onClick={() => setEditing(false)}
          disabled={saving}
          className="rounded px-2 py-0.5 text-[10px] uppercase tracking-eyebrow text-text-dim hover:text-text disabled:opacity-50"
        >
          Cancel
        </button>
        {value && (
          <button
            onClick={clear}
            disabled={saving}
            className="rounded px-2 py-0.5 text-[10px] uppercase tracking-eyebrow text-red/80 hover:text-red disabled:opacity-50"
            title="Remove the assertion entirely"
          >
            Clear
          </button>
        )}
      </span>
    );
  }

  if (!value) {
    return (
      <button
        onClick={() => setEditing(true)}
        className="text-[10px] uppercase tracking-eyebrow text-text-dim underline decoration-dotted underline-offset-4 hover:text-green"
        title="Declare what your account had when Veritas first connected. Used to compute trading return without including pre-bridge funding."
      >
        + Declare starting capital
      </button>
    );
  }

  return (
    <span title={`User-declared starting balance as of ${value.as_of_date}. Used as the baseline for trading-return calcs. Not chain-attested.`}>
      Starting capital{' '}
      <b className="font-medium text-text">{fmtMoney(value.asserted_balance, '')}</b>
      <span className="ml-1 text-[10px] uppercase tracking-eyebrow text-text-dim">
        · {value.as_of_date} declared
      </span>
      <button
        onClick={() => setEditing(true)}
        className="ml-2 text-[10px] uppercase tracking-eyebrow text-text-dim underline decoration-dotted underline-offset-4 hover:text-green"
        title="Edit starting capital"
      >
        edit
      </button>
    </span>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Account title + category editor (M2).
//
// Replaces the static "Account {login}" H1 with the caller's own friendly
// label (display_label) when set, plus an optional category chip. Pencil
// affordance opens an inline editor with a label input + category combobox.
// Category autocomplete is browser-native via <datalist>, populated from the
// caller's previously-used categories (one RPC at mount, kept in sync after
// each save). Write goes through veritas.set_account_user_metadata which
// UPSERTs into account_user_metadata under the caller's RLS. Empty input
// clears the field (UI falls back to "Account {login}" / no chip).
function AccountTitleEditor({
  accountLogin,
  value,
  knownCategories,
  readOnly,
  onChange,
  onError,
}: {
  accountLogin: number;
  value: { display_label: string | null; category: string | null; single_system: boolean } | null;
  knownCategories: string[];
  readOnly?: boolean;
  onChange: (
    next: { display_label: string | null; category: string | null; single_system: boolean },
    allCats?: string[],
  ) => void;
  onError: (msg: string | null) => void;
}) {
  const [editing, setEditing]   = useState(false);
  const [label,   setLabel]     = useState(value?.display_label ?? '');
  const [category, setCategory] = useState(value?.category ?? '');
  const [singleSystem, setSingleSystem] = useState(value?.single_system ?? false);
  const [saving,  setSaving]    = useState(false);

  // Re-sync local form state if the parent loads / changes value.
  useEffect(() => {
    setLabel(value?.display_label ?? '');
    setCategory(value?.category ?? '');
    setSingleSystem(value?.single_system ?? false);
  }, [value?.display_label, value?.category, value?.single_system]);

  const displayLabel = (value?.display_label ?? '').trim();
  const titleText    = displayLabel || `Account ${accountLogin}`;
  const datalistId   = `account-categories-${accountLogin}`;

  async function save() {
    const trimmedLabel    = label.trim();
    const trimmedCategory = category.trim();
    setSaving(true);
    const r = await veritas.rpc('set_account_user_metadata', {
      p_account_login: accountLogin,
      p_display_label: trimmedLabel.length    ? trimmedLabel    : null,
      p_category:      trimmedCategory.length ? trimmedCategory : null,
      p_single_system: singleSystem,
    });
    setSaving(false);
    if (r.error) { onError(r.error.message); return; }
    const next = {
      display_label: trimmedLabel.length    ? trimmedLabel    : null,
      category:      trimmedCategory.length ? trimmedCategory : null,
      single_system: singleSystem,
    };
    // Keep the known-categories list in sync so a brand-new category appears
    // in the autocomplete immediately on next edit, without re-querying.
    const cats =
      trimmedCategory.length && !knownCategories.includes(trimmedCategory)
        ? [...knownCategories, trimmedCategory].sort()
        : knownCategories;
    onChange(next, cats);
    setEditing(false);
    onError(null);
  }

  // Read-only viewers: show the title + category as static text, no pencil.
  if (readOnly) {
    return (
      <div className="mt-2 flex flex-wrap items-baseline gap-3">
        <h1 className="text-[22px] font-bold tracking-tight">{titleText}</h1>
        {displayLabel && (
          <span className="text-[11px] text-text-dim">Account {accountLogin}</span>
        )}
        {value?.category && (
          <span className="rounded-full border border-line bg-bg-2 px-2 py-[1px] text-[10px] font-semibold uppercase tracking-eyebrow text-text-mid">
            {value.category}
          </span>
        )}
        {value?.single_system && (
          <span
            className="rounded-full border border-blue/40 bg-blue/10 px-2 py-[1px] text-[10px] font-semibold uppercase tracking-eyebrow text-blue"
            title="Bots are collapsed into one system in the Per-Bot panel."
          >
            System
          </span>
        )}
      </div>
    );
  }

  if (editing) {
    return (
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <input
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder={`Account ${accountLogin}`}
          maxLength={120}
          className="w-72 rounded border border-line bg-bg px-2 py-1 text-[18px] font-bold text-text outline-none focus:border-green"
          disabled={saving}
          autoFocus
        />
        <input
          type="text"
          list={datalistId}
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          placeholder="Category (e.g. Prop firm)"
          maxLength={60}
          className="w-48 rounded border border-line bg-bg px-2 py-1 text-[12px] text-text outline-none focus:border-green"
          disabled={saving}
        />
        <datalist id={datalistId}>
          {knownCategories.map((c) => <option key={c} value={c} />)}
        </datalist>
        <label
          className="flex items-center gap-1.5 text-[10px] text-text-mid"
          title="Treat all of this account's bots as one system (e.g. TradeFlow): the Per-Bot panel folds every EA magic into a single system row and keeps Manual separate. Drawdown shown is the combined-book account figure, not a sum of per-bot DDs."
        >
          <input
            type="checkbox"
            checked={singleSystem}
            onChange={(e) => setSingleSystem(e.target.checked)}
            disabled={saving}
            className="accent-green"
          />
          One system
        </label>
        <button
          onClick={save}
          disabled={saving}
          className="rounded bg-green/20 px-2 py-1 text-[10px] font-bold uppercase tracking-eyebrow text-green hover:bg-green/30 disabled:opacity-50"
        >
          {saving ? '…' : 'Save'}
        </button>
        <button
          onClick={() => {
            setLabel(value?.display_label ?? '');
            setCategory(value?.category ?? '');
            setSingleSystem(value?.single_system ?? false);
            setEditing(false);
            onError(null);
          }}
          disabled={saving}
          className="rounded px-2 py-1 text-[10px] uppercase tracking-eyebrow text-text-dim hover:text-text disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    );
  }

  return (
    <div className="mt-2 flex flex-wrap items-baseline gap-3">
      <h1 className="text-[22px] font-bold tracking-tight">{titleText}</h1>
      {displayLabel && (
        <span className="text-[11px] text-text-dim">Account {accountLogin}</span>
      )}
      {value?.category && (
        <span className="rounded-full border border-line bg-bg-2 px-2 py-[1px] text-[10px] font-semibold uppercase tracking-eyebrow text-text-mid">
          {value.category}
        </span>
      )}
      {value?.single_system && (
        <span
          className="rounded-full border border-blue/40 bg-blue/10 px-2 py-[1px] text-[10px] font-semibold uppercase tracking-eyebrow text-blue"
          title="Bots are collapsed into one system in the Per-Bot panel."
        >
          System
        </span>
      )}
      <button
        onClick={() => setEditing(true)}
        className="text-[10px] uppercase tracking-eyebrow text-text-dim underline decoration-dotted underline-offset-4 hover:text-green"
        title="Edit label and category"
      >
        edit
      </button>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Per-credential filter chips. Shown above the KPI grid when an account has
// more than one credential (typical: a `demo` seed credential plus one or
// more live `Roboforex account`-style installs). Clicking a chip swaps the
// `selectedCredId` which the parent uses to scope its data fetches.
function CredentialFilter({
  credentials,
  selected,
  onSelect,
}: {
  credentials: CredentialOption[];
  selected: string;
  onSelect: (id: string) => void;
}) {
  return (
    <div className="flex items-center gap-2 text-[10px] uppercase tracking-eyebrow text-text-dim">
      <span>Credential</span>
      <Chips>
        <Chip active={selected === 'all'} onClick={() => onSelect('all')}>
          All
        </Chip>
        {credentials.map((c) => (
          <Chip
            key={c.id}
            active={selected === c.id}
            onClick={() => onSelect(c.id)}
          >
            <span className={c.revoked_at ? 'opacity-60' : ''}>
              {c.install_label}
              {c.revoked_at ? ' · revoked' : ''}
            </span>
          </Chip>
        ))}
      </Chips>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Magic-number filter. Native <select> styled to match the dark theme.
// Each option shows the magic, the trade count on this account, and the
// cumulative P&L — enough metadata to identify a bot at a glance even
// without a friendly label. Magic 0 is rendered as "Manual" since that's
// what MT5 uses for human-placed trades.
function MagicFilter({
  magics,
  selected,
  onSelect,
}: {
  magics: { magic: number; count: number; pnl: number }[];
  selected: number | 'all';
  onSelect: (m: number | 'all') => void;
}) {
  const totalTrades = magics.reduce((a, m) => a + m.count, 0);
  const totalPnL = magics.reduce((a, m) => a + m.pnl, 0);

  return (
    <div className="flex items-center gap-2 text-[10px] uppercase tracking-eyebrow text-text-dim">
      <span>Magic</span>
      <select
        value={selected === 'all' ? 'all' : String(selected)}
        onChange={(e) => {
          const v = e.target.value;
          onSelect(v === 'all' ? 'all' : Number(v));
        }}
        className="num cursor-pointer rounded-md border border-line bg-bg-2 px-3 py-[5px] text-[10.5px] font-semibold uppercase tracking-[0.06em] text-text outline-none transition-colors hover:border-green focus:border-green"
      >
        <option value="all">
          All magics  ({totalTrades} trades, {totalPnL >= 0 ? '+' : ''}{Math.round(totalPnL).toLocaleString('en-US')})
        </option>
        {magics.map((m) => (
          <option key={m.magic} value={m.magic}>
            {m.magic === 0 ? 'Manual (no magic)' : `Magic ${m.magic}`}
            {' '}·{' '}{m.count} trades  ·  {m.pnl >= 0 ? '+' : ''}{Math.round(m.pnl).toLocaleString('en-US')}
          </option>
        ))}
      </select>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
function SourceCard({
  kind,
  seg,
  total,
}: {
  kind: 'bot' | 'manual';
  seg: { count: number; profit: number; winRate: number; volume: number };
  total: number;
}) {
  const share = total ? (seg.count / total) * 100 : 0;
  return (
    <div className="rounded-md border border-line-soft bg-bg-2 px-3.5 py-3">
      <div className="mb-1 flex items-center justify-between text-[10px] font-bold uppercase tracking-eyebrow">
        <span className={kind === 'bot' ? 'text-blue' : 'text-amber'}>
          {kind === 'bot' ? 'Bot' : 'Manual'}
        </span>
        <span className="text-text-dim">{share.toFixed(1)}%</span>
      </div>
      <div className="num text-[18px] font-bold leading-none">
        <span className={seg.profit >= 0 ? 'text-green' : 'text-red'}>
          {seg.profit >= 0 ? '+' : ''}{fmtMoney(seg.profit, '')}
        </span>
      </div>
      <div className="mt-2 grid grid-cols-3 gap-2 text-[10.5px] text-text-dim">
        <div><span className="block text-text">{seg.count}</span>trades</div>
        <div><span className="block text-text">{seg.winRate.toFixed(1)}%</span>win rate</div>
        <div><span className="block text-text">{seg.volume.toFixed(2)}</span>lots</div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// PerBotPanel (S8) — one row per magic, sorted by lifetime net P&L. Clicking
// a row scopes the whole page to that bot via the magic filter (click the
// active row again to clear). Descriptive: the Signal column surfaces decay /
// dormancy from the trader's own data; it never tells them to retire a bot.
// ════════════════════════════════════════════════════════════════════════
// PerBotPanel + BotSignal extracted to components/account/per-bot-panel.tsx
// (2026-05-28 refactor — no behavioral change). Imported above.

// SortKey / JournalView types + TradeJournal / TradeTableInner / PositionsTable
// (and the shared <Th> cell helper) extracted to
// components/account/trade-journal.tsx (2026-05-28 refactor — no behavioral
// change). TradeJournal is imported above.

// ════════════════════════════════════════════════════════════════════════
// RiskMetricsPanel — risk-adjusted performance tiles. All values derive
// from the snapshot equity series (account-level, not Source-scoped).
//
// Tone rules (rough trader heuristics, not a hedge-fund prospectus):
//   Sharpe   > 1   green   |  > 0   blue   |  < 0     red
//   Sortino  > 1   green   |  > 0   blue   |  < 0     red
//   Recovery > 2   green   |  > 1   blue   |  < 1     amber
//   Calmar   > 1   green   |  > 0   blue   |  < 0     red
//   Max DD duration  >60d  amber  |  >120d  red   |  else  blue
//   Profitable day % >55   green  |  >50    blue  |  >45   amber  | else red
// ════════════════════════════════════════════════════════════════════════
function RiskMetricsPanel({ metrics }: { metrics: RiskMetrics }) {
  // Need at least a couple of days for any of this to make sense. Hide the
  // panel entirely on day-1 accounts so the dashboard isn't full of em-dashes.
  if (metrics.totalDays < 2) return null;

  type Tone = 'green' | 'blue' | 'amber' | 'red';

  const fmtRatio = (v: number | null) =>
    v == null ? '—' : `${v >= 0 ? '' : ''}${v.toFixed(2)}`;
  const fmtDays = (v: number | null) =>
    v == null || v === 0 ? '—' : `${v}d`;
  const fmtPctVal = (v: number | null) =>
    v == null ? '—' : `${v.toFixed(1)}%`;

  const ratioTone = (v: number | null, good = 1): Tone => {
    if (v == null) return 'blue';
    if (v >= good * 2) return 'green';
    if (v >= good) return 'green';
    if (v >= 0) return 'blue';
    return 'red';
  };
  const recoveryTone = (v: number | null): Tone => {
    if (v == null) return 'blue';
    if (v >= 2) return 'green';
    if (v >= 1) return 'blue';
    if (v >= 0) return 'amber';
    return 'red';
  };
  const ddDaysTone = (v: number | null): Tone => {
    if (v == null) return 'blue';
    if (v > 120) return 'red';
    if (v > 60) return 'amber';
    return 'blue';
  };
  const posDaysTone = (v: number | null): Tone => {
    if (v == null) return 'blue';
    if (v >= 55) return 'green';
    if (v >= 50) return 'blue';
    if (v >= 45) return 'amber';
    return 'red';
  };

  const ddSub =
    metrics.ongoingDdDays > 0
      ? `${metrics.ongoingDdDays}d below peak now`
      : 'at or near peak';

  return (
    <Panel>
      <PanelHead
        title={
          <>Risk-adjusted performance{' '}
          <span className="text-text-dim">
            {metrics.totalDays}d · {metrics.cumReturnPct >= 0 ? '+' : ''}
            {metrics.cumReturnPct.toFixed(1)}% cum.
          </span></>
        }
      />
      <PanelBody>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <Kpi
            label="Sharpe (ann.)"
            value={fmtRatio(metrics.sharpe)}
            sub="risk-adjusted"
            tone={ratioTone(metrics.sharpe, 1)}
          />
          <Kpi
            label="Sortino (ann.)"
            value={fmtRatio(metrics.sortino)}
            sub="downside-only"
            tone={ratioTone(metrics.sortino, 1)}
          />
          <Kpi
            label="Recovery factor"
            value={fmtRatio(metrics.recoveryFactor)}
            sub="return ÷ max DD"
            tone={recoveryTone(metrics.recoveryFactor)}
          />
          <Kpi
            label="Calmar (ann.)"
            value={fmtRatio(metrics.calmar)}
            sub="ann. ret ÷ max DD"
            tone={ratioTone(metrics.calmar, 1)}
          />
          <Kpi
            label="Max DD duration"
            value={fmtDays(metrics.maxDdDurationDays)}
            sub={ddSub}
            tone={ddDaysTone(metrics.maxDdDurationDays)}
          />
          <Kpi
            label="Profitable days"
            value={fmtPctVal(metrics.positiveDayPct)}
            sub={`${metrics.upDays}↑ / ${metrics.downDays}↓ · ${metrics.totalDays} trading days`}
            tone={posDaysTone(metrics.positiveDayPct)}
          />
        </div>
        {/* Provenance footer — a truth product states its basis. Trading-day
            denominator per the L35 fix (flat weekend/dormant dates dropped);
            ratio caveat is the L35 open item surfaced descriptively: a daily
            BALANCE curve has artificially low volatility for grid/martingale
            bots (smooth step-ups, floating pain invisible), so Sharpe/Sortino/
            Calmar run optimistic. Labeled, not hidden. */}
        <p className="mt-3 border-t border-line-soft pt-2.5 text-[11px] leading-relaxed text-text-dim">
          Basis: <b className="font-medium text-text-mid">{metrics.totalDays} trading days</b>
          {metrics.flatDaysExcluded > 0 && (
            <> ({metrics.flatDaysExcluded} flat weekend/dormant {metrics.flatDaysExcluded === 1 ? 'day' : 'days'} excluded)</>
          )}
          {' '}· deposit-adjusted · daily <b className="font-medium text-text-mid">balance</b> curve.{' '}
          <span className="text-amber" title="A balance curve only steps when positions close — open floating drawdown never appears in the daily returns. For grid/martingale bots that means artificially smooth returns, so annualised Sharpe/Sortino/Calmar overstate risk-adjusted quality. The Max Drawdown card (equity-inclusive) is the honest risk figure.">
            Sharpe/Sortino/Calmar on a balance-only curve run optimistic — floating drawdown is invisible to daily returns.
          </span>
        </p>
      </PanelBody>
    </Panel>
  );
}

// ════════════════════════════════════════════════════════════════════════
// BehaviorPanels — tag analytics + mistake correlation. Sits between the
// chart row and the trade journal. Surfaces the *why* — which tags actually
// move your P&L, which mistakes correlate with bad outcomes.
// ════════════════════════════════════════════════════════════════════════
function BehaviorPanels({
  stats,
  mistakes,
  scopeLabel,
}: {
  stats: TagStatsRow[];
  mistakes: MistakeRow[];
  scopeLabel: 'all' | 'manual' | 'bot';
}) {
  // Per-tag stats + mistake correlation are server-computed
  // (account_behavior_analytics) and arrive already filtered to applied tags.

  // Hide the whole row when there's nothing to say yet — first install,
  // no tags applied. Avoids two empty boxes screaming for attention.
  if (stats.length === 0 && mistakes.length === 0) return null;

  return (
    <div className="grid gap-3.5 lg:grid-cols-[3fr_2fr]">
      <Panel>
        <PanelHead
          title={
            <>Tag performance{' '}
            <span className="text-text-dim">
              P&amp;L by label{scopeLabel !== 'all' ? ` · ${scopeLabel} only` : ''}
            </span></>
          }
        />
        <PanelBody>
          {stats.length === 0 ? (
            <div className="py-4 text-center text-[11.5px] text-text-dim">
              Tag your positions and aggregate stats appear here. Open a
              position from the journal below and click the tag chip to add a
              setup, session, or mistake label.
            </div>
          ) : (
            <div className="space-y-2">
              {stats.slice(0, 8).map((s) => (
                <div
                  key={s.tagId}
                  className="grid grid-cols-[minmax(120px,_1.4fr)_repeat(3,_minmax(0,_1fr))] items-center gap-2 rounded-md border border-line-soft bg-bg-2 px-3 py-2"
                >
                  <span className="inline-flex items-center gap-2">
                    <span
                      className="rounded-[3px] px-1.5 py-px text-[10px] font-semibold uppercase tracking-eyebrow"
                      style={{ backgroundColor: `#${s.color}20`, color: `#${s.color}` }}
                    >
                      {s.tagName}
                    </span>
                    {s.category && (
                      <span className="text-[9px] uppercase tracking-eyebrow text-text-dim">
                        {s.category}
                      </span>
                    )}
                  </span>
                  <span className="num text-right text-[11.5px] text-text-mid">
                    {s.count} trades
                  </span>
                  <span className="num text-right text-[11.5px] text-text-mid">
                    {s.winRate.toFixed(1)}% win
                  </span>
                  <span className={cn(
                    'num text-right text-[12px] font-semibold',
                    s.totalPnL >= 0 ? 'text-green' : 'text-red',
                  )}>
                    {s.totalPnL >= 0 ? '+' : ''}{s.totalPnL.toFixed(2)}
                    {s.expectancyR != null && (
                      <span className="ml-2 text-[10px] font-normal text-text-dim">
                        {fmtRMultiple(s.expectancyR)}
                      </span>
                    )}
                  </span>
                </div>
              ))}
            </div>
          )}
        </PanelBody>
      </Panel>

      <Panel>
        <PanelHead
          title={
            <>Mistakes{' '}
            <span className="text-text-dim">
              tagged vs untagged{scopeLabel !== 'all' ? ` · ${scopeLabel} only` : ''}
            </span></>
          }
        />
        <PanelBody>
          {mistakes.length === 0 ? (
            <div className="py-4 text-center text-[11.5px] text-text-dim">
              When you flag a position with a mistake tag (moved-stop,
              revenge-trade, oversized…), the average outcome of those trades
              vs the rest appears here.
            </div>
          ) : (
            <div className="space-y-2.5">
              {mistakes.map((m) => {
                const verdict =
                  m.pnlDelta < -1 ? 'hurts' :
                  m.pnlDelta > 1  ? 'helps?' : 'neutral';
                const verdictColor =
                  verdict === 'hurts' ? 'text-red'
                  : verdict === 'helps?' ? 'text-amber'
                  : 'text-text-dim';
                return (
                  <div
                    key={m.tagName}
                    className="rounded-md border border-line-soft bg-bg-2 px-3 py-2.5"
                  >
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="rounded-[3px] bg-red/[0.10] px-1.5 py-px text-[10px] font-semibold uppercase tracking-eyebrow text-red">
                        {m.tagName}
                      </span>
                      <span className={cn('text-[10px] font-bold uppercase tracking-eyebrow', verdictColor)}>
                        {verdict}
                      </span>
                    </div>
                    <div className="mt-1.5 flex items-baseline justify-between text-[11px]">
                      <span className="text-text-dim">
                        {m.taggedCount} tagged
                      </span>
                      <span className="num">
                        <span className={m.taggedAvgPnL >= 0 ? 'text-green' : 'text-red'}>
                          {m.taggedAvgPnL >= 0 ? '+' : ''}{m.taggedAvgPnL.toFixed(2)}
                        </span>
                        <span className="text-text-dim"> avg vs </span>
                        <span className={m.untaggedAvgPnL >= 0 ? 'text-green' : 'text-red'}>
                          {m.untaggedAvgPnL >= 0 ? '+' : ''}{m.untaggedAvgPnL.toFixed(2)}
                        </span>
                      </span>
                    </div>
                    {m.rDelta != null && (
                      <div className="mt-0.5 text-[10.5px] text-text-dim">
                        ΔR per trade: <span className={m.rDelta < 0 ? 'text-red' : 'text-green'}>
                          {fmtRMultiple(m.rDelta)}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </PanelBody>
      </Panel>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// CalendarHeatmap — N month-grids side-by-side. Each day cell tinted by
// signed P&L intensity. Day-of-week aligned columns make weekday patterns
// jump out: "I lose every Monday for 6 weeks running" is visible here in
// a way no bar chart can match.
// ════════════════════════════════════════════════════════════════════════
function CalendarHeatmap({
  dayMap,
  months = 3,
}: {
  dayMap: Map<string, DayBucket>;
  months?: number;
}) {
  // Color scale: linear from -maxAbs..+maxAbs. Compute over ALL days in the
  // visible window so the gradient is consistent across the three months.
  const monthsList = useMemo(() => recentMonths(months), [months]);
  const maxAbs = useMemo(() => {
    let m = 0;
    for (const yyMM of monthsList) {
      const [y, mo] = yyMM.split('-').map(Number);
      const days = new Date(Date.UTC(y, mo, 0)).getUTCDate();
      for (let d = 1; d <= days; d++) {
        const key = `${yyMM}-${String(d).padStart(2, '0')}`;
        const v = dayMap.get(key)?.profit ?? 0;
        if (Math.abs(v) > m) m = Math.abs(v);
      }
    }
    return m || 1;  // never 0 (avoid divide-by-zero in cell shading)
  }, [dayMap, monthsList]);

  return (
    <Panel>
      <PanelHead
        title={
          <>Calendar <span className="text-text-dim">last {months} months · P&amp;L by day</span></>
        }
        right={
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-eyebrow text-text-dim">
            <span className="inline-flex items-center gap-1">
              <span className="h-2 w-2 rounded-sm bg-red/70" /> loss
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="h-2 w-2 rounded-sm bg-green/70" /> profit
            </span>
          </div>
        }
      />
      <PanelBody>
        <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
          {monthsList.map((yyMM) => (
            <MonthGrid
              key={yyMM}
              yyMM={yyMM}
              dayMap={dayMap}
              maxAbs={maxAbs}
            />
          ))}
        </div>
      </PanelBody>
    </Panel>
  );
}

function MonthGrid({
  yyMM,
  dayMap,
  maxAbs,
}: {
  yyMM: string;
  dayMap: Map<string, DayBucket>;
  maxAbs: number;
}) {
  const [y, mo] = yyMM.split('-').map(Number);
  const monthName = new Date(Date.UTC(y, mo - 1, 1)).toLocaleString('en-US', {
    month: 'long', year: 'numeric', timeZone: 'UTC',
  });
  const daysInMonth = new Date(Date.UTC(y, mo, 0)).getUTCDate();
  // First day-of-week, ISO style (Mon=0..Sun=6)
  const firstDow = (new Date(Date.UTC(y, mo - 1, 1)).getUTCDay() + 6) % 7;

  // Build cells: pad with nulls before day 1, then 1..daysInMonth
  const cells: Array<{ day: number; key: string; bucket: ReturnType<typeof dayMap.get> } | null> = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const key = `${yyMM}-${String(d).padStart(2, '0')}`;
    cells.push({ day: d, key, bucket: dayMap.get(key) });
  }
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <div>
      <div className="mb-2 text-[10.5px] font-bold uppercase tracking-eyebrow text-text-mid">
        {monthName}
      </div>
      <div className="mb-1 grid grid-cols-7 gap-1 text-[8.5px] uppercase tracking-eyebrow text-text-dim">
        {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => (
          <div key={i} className="text-center">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((c, i) => {
          if (!c) return <div key={i} className="aspect-square rounded-[3px] bg-transparent" />;
          const profit = c.bucket?.profit ?? 0;
          const trades = c.bucket?.trades ?? 0;
          const intensity = Math.min(1, Math.abs(profit) / maxAbs);
          const tone = profit > 0 ? '0,255,136' : profit < 0 ? '239,68,68' : null;
          const bg = tone
            ? `rgba(${tone}, ${0.12 + intensity * 0.55})`
            : trades > 0 ? 'rgba(255,255,255,0.04)' : 'transparent';
          const border = trades === 0 ? '1px dashed rgba(255,255,255,0.06)' : 'none';
          return (
            <div
              key={i}
              title={trades === 0
                ? `${c.key} · no trades`
                : `${c.key} · ${trades} trade${trades === 1 ? '' : 's'} · ${profit >= 0 ? '+' : ''}${profit.toFixed(2)}`}
              className="relative aspect-square overflow-hidden rounded-[3px] text-[9px]"
              style={{ background: bg, border }}
            >
              <div className="absolute left-1 top-0.5 text-text-dim">{c.day}</div>
              {trades > 0 && (
                <div className={cn(
                  'absolute inset-x-0 bottom-0.5 truncate px-1 text-center font-mono tabular-nums text-[9px]',
                  profit > 0 ? 'text-green' : profit < 0 ? 'text-red' : 'text-text-mid',
                )}>
                  {profit >= 0 ? '+' : ''}{Math.abs(profit) >= 1000 ? (profit / 1000).toFixed(1) + 'k' : profit.toFixed(0)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// CumulativeIntradayPanel — average cumulative P&L through a trading day,
// hour-of-day on x. Reveals shape: build then give back, grind through,
// only-print-at-close, etc.
// ════════════════════════════════════════════════════════════════════════
function CumulativeIntradayPanel({
  data,
}: {
  data: IntradayPoint[];
}) {
  const days = data[0]?.days ?? 0;
  const final = data[23]?.avgCumulative ?? 0;

  // Find peak / trough hours for the insight subtitle
  const peakHour = data.reduce((best, d) => d.avgCumulative > best.avgCumulative ? d : best, data[0]);
  const troughHour = data.reduce((worst, d) => d.avgCumulative < worst.avgCumulative ? d : worst, data[0]);

  const insight = (() => {
    if (days === 0) return 'No closed-position data yet.';
    const peakIsLast = peakHour && Math.abs(peakHour.avgCumulative - final) < 0.5;
    const troughBeforePeak = troughHour && peakHour && troughHour.hour < peakHour.hour;
    if (peakIsLast) return 'Average day grinds toward close';
    if (peakHour && peakHour.avgCumulative > final + 1) return `Peak at ${peakHour.hour}:00 UTC, gives some back by close`;
    if (troughHour && troughHour.avgCumulative < -1 && !troughBeforePeak) return `Worst at ${troughHour.hour}:00 UTC`;
    return 'Average day shape';
  })();

  return (
    <Panel>
      <PanelHead
        title={
          <>Cumulative P&amp;L through day <span className="text-text-dim">avg over {days} days · UTC</span></>
        }
      />
      <PanelBody>
        <div className="h-[240px] sm:h-[260px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data}>
              <defs>
                <linearGradient id="intraday-pos" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"   stopColor="#00ff88" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="#00ff88" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="intraday-neg" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"   stopColor="#ef4444" stopOpacity={0} />
                  <stop offset="100%" stopColor="#ef4444" stopOpacity={0.25} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke={GRID} vertical={false} />
              <XAxis dataKey="hour" tick={AXIS} tickFormatter={(v) => `${v}h`} />
              <YAxis tick={AXIS} width={56} />
              <Tooltip
                contentStyle={TooltipStyle}
                formatter={(v: number) => [fmtMoney(v, ''), 'Avg cumulative']}
                labelFormatter={(h) => `${h}:00 UTC`}
              />
              {/* Two stacked areas — one for pos region, one for neg — so
                  the fill flips color at the zero line. The line itself is
                  a single neutral stroke that traces both. */}
              <Area
                type="monotone"
                dataKey="avgCumulative"
                stroke="#10b981"
                strokeWidth={2}
                fill="url(#intraday-pos)"
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-2 text-[10.5px] text-text-dim">{insight}</div>
      </PanelBody>
    </Panel>
  );
}

// <Th> table-header cell helper moved to components/account/trade-journal.tsx
// (PositionsTable was its only consumer).

// ════════════════════════════════════════════════════════════════════════
// AlertsPanel — settable in-app alerts. Add/edit/delete rules; each rule's
// breach status is computed live against the latest snapshot. NO email
// (MT5 already does that via SendMail/SendNotification at the EA level).
// ════════════════════════════════════════════════════════════════════════
function AlertsPanel({
  rules,
  latestSnapshot,
  dayPnlTrading,
  defaultCredentialId,
  credentials,
  onChanged,
  onDeleted,
  onError,
}: {
  rules: AlertRule[];
  latestSnapshot: AccountSnapshot | undefined;
  // Trading-only Today P&L (deposits/withdrawals stripped). Drives daily_loss.
  dayPnlTrading: number | null;
  defaultCredentialId: string | null;
  credentials: CredentialOption[];
  onChanged: (rule: AlertRule) => void;
  onDeleted: (id: string) => void;
  onError: (msg: string) => void;
}) {
  const [showForm, setShowForm] = useState(false);
  const [kind, setKind] = useState<AlertKind>('drawdown_pct');
  const [threshold, setThreshold] = useState<string>('5');
  const [cooldown, setCooldown] = useState<string>('60');
  const [credId, setCredId] = useState<string | null>(defaultCredentialId);

  // keep the form's default cred in sync if the parent changes the selection
  useEffect(() => { setCredId(defaultCredentialId); }, [defaultCredentialId]);

  async function add() {
    if (!credId) { onError('Pick a credential first.'); return; }
    const t = Number(threshold);
    const c = Number(cooldown);
    if (!Number.isFinite(t)) { onError('Threshold must be numeric.'); return; }
    if (!Number.isFinite(c) || c < 0) { onError('Cooldown must be ≥ 0.'); return; }
    const { data, error } = await veritas
      .from('alert_rules')
      .insert({ credential_id: credId, kind, threshold: t, cooldown_minutes: c })
      .select()
      .maybeSingle();
    if (error) { onError(error.message); return; }
    if (data) onChanged(data as AlertRule);
    setShowForm(false);
  }

  async function toggle(r: AlertRule) {
    const { data, error } = await veritas
      .from('alert_rules')
      .update({ enabled: !r.enabled })
      .eq('id', r.id)
      .select()
      .maybeSingle();
    if (error) { onError(error.message); return; }
    if (data) onChanged(data as AlertRule);
  }

  async function remove(id: string) {
    if (!confirm('Delete this alert rule?')) return;
    const { error } = await veritas.from('alert_rules').delete().eq('id', id);
    if (error) { onError(error.message); return; }
    onDeleted(id);
  }

  return (
    <Panel>
      <PanelHead
        title={
          <>
            Alerts <span className="text-text-dim">
              {rules.length === 0 ? 'none configured'
                : `${rules.filter((r) => r.enabled && evaluateAlertRule(r, latestSnapshot, dayPnlTrading).breached).length} breached, ${rules.length} total`}
            </span>
          </>
        }
        right={
          <button
            onClick={() => setShowForm((s) => !s)}
            className="rounded-md border border-line bg-bg-2 px-3 py-1.5 text-[11px] uppercase tracking-eyebrow text-text-mid hover:border-green hover:text-green"
          >
            {showForm ? 'Cancel' : '+ New rule'}
          </button>
        }
      />
      <PanelBody>
        {showForm && (
          <div className="mb-4 rounded-md border border-line bg-bg-2 p-3">
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              <label className="text-[10px] uppercase tracking-eyebrow text-text-dim">
                Type
                <select
                  value={kind}
                  onChange={(e) => setKind(e.target.value as AlertKind)}
                  className="mt-1 block w-full rounded border border-line bg-bg px-2 py-1.5 text-[12px] text-text"
                >
                  <option value="drawdown_pct">Drawdown %</option>
                  <option value="daily_loss">Daily P&L floor</option>
                  <option value="offline_minutes">Account offline (min)</option>
                </select>
              </label>
              <label className="text-[10px] uppercase tracking-eyebrow text-text-dim">
                Threshold ({alertKindUnit(kind) || 'currency'})
                <input
                  type="number"
                  step="any"
                  value={threshold}
                  onChange={(e) => setThreshold(e.target.value)}
                  className="num mt-1 block w-full rounded border border-line bg-bg px-2 py-1.5 text-[12px] text-text"
                />
              </label>
              <label className="text-[10px] uppercase tracking-eyebrow text-text-dim">
                Cooldown (min)
                <input
                  type="number"
                  min={0}
                  value={cooldown}
                  onChange={(e) => setCooldown(e.target.value)}
                  className="num mt-1 block w-full rounded border border-line bg-bg px-2 py-1.5 text-[12px] text-text"
                />
              </label>
              <label className="text-[10px] uppercase tracking-eyebrow text-text-dim">
                Credential
                <select
                  value={credId ?? ''}
                  onChange={(e) => setCredId(e.target.value || null)}
                  className="mt-1 block w-full rounded border border-line bg-bg px-2 py-1.5 text-[12px] text-text"
                >
                  {credentials.map((c) => (
                    <option key={c.id} value={c.id}>{c.install_label}</option>
                  ))}
                </select>
              </label>
            </div>
            <div className="mt-3 flex items-center justify-between">
              <p className="text-[10.5px] text-text-dim">
                Fires {alertKindDirection(kind)} threshold. Surfaced in-app only;
                MT5 still handles its own SendMail/SendNotification.
              </p>
              <button
                onClick={add}
                className="rounded-md bg-green px-3 py-1.5 text-[12px] font-semibold text-bg hover:opacity-90"
              >
                Add rule
              </button>
            </div>
          </div>
        )}

        {rules.length === 0 ? (
          <div className="py-6 text-center text-[11.5px] text-text-dim">
            No alert rules yet. Add one above to get notified about drawdown,
            daily loss floor, or an account going offline.
          </div>
        ) : (
          <div className="space-y-2">
            {rules.map((r) => {
              const ev = evaluateAlertRule(r, latestSnapshot, dayPnlTrading);
              return (
                <div
                  key={r.id}
                  className={cn(
                    'flex flex-wrap items-center gap-3 rounded-md border px-3 py-2.5',
                    ev.breached
                      ? 'border-red/40 bg-red/[0.06]'
                      : 'border-line bg-bg-2',
                  )}
                >
                  <span
                    className={cn(
                      'inline-flex h-2 w-2 rounded-full',
                      !r.enabled ? 'bg-text-faint'
                        : ev.breached ? 'bg-red animate-pulse'
                        : 'bg-green',
                    )}
                    title={r.enabled ? (ev.breached ? 'Breached' : 'OK') : 'Disabled'}
                  />
                  <span className="text-[11px] font-bold uppercase tracking-eyebrow text-text">
                    {alertKindLabel(r.kind)}
                  </span>
                  <span className="num text-[11.5px] text-text-mid">
                    threshold {r.threshold}{alertKindUnit(r.kind)}
                  </span>
                  <span
                    className={cn(
                      'num flex-1 text-[11px]',
                      ev.breached ? 'text-red' : 'text-text-dim',
                    )}
                  >
                    {ev.message}
                  </span>
                  <button
                    onClick={() => toggle(r)}
                    className="inline-flex min-h-[40px] items-center px-2 text-[11px] uppercase tracking-eyebrow text-text-dim hover:text-text md:min-h-0 md:px-0 md:text-[10.5px]"
                  >
                    {r.enabled ? 'Disable' : 'Enable'}
                  </button>
                  <button
                    onClick={() => remove(r.id)}
                    className="inline-flex min-h-[40px] items-center px-2 text-[11px] uppercase tracking-eyebrow text-text-dim hover:text-red md:min-h-0 md:px-0 md:text-[10.5px]"
                  >
                    Delete
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </PanelBody>
    </Panel>
  );
}
